import React, { useState, useEffect, useCallback } from 'react';
import { saveClientData, loadClientData, createClient, loadAllClients, deleteClient, loginCoach, logoutCoach } from './firebase';

// Service worker non activé

// ─── OFFLINE DETECTION ──────────────────────────────────────────────────────
function useOnlineStatus() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  React.useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return isOnline;
}

// ─── STYLES ────────────────────────────────────────────────────────────────
const CSS = `
  :root {
    --bordeaux: #7A3B5A;
    --rose-vieux: #C4899A;
    --rose-moyen: #D4A0AF;
    --rose-pale: #F2D4DA;
    --rose-poudre: #FAF0F2;
    --or: #C4899A;
    --or-clair: #F2D4DA;
    --creme: #FDF8F9;
    --creme-fonce: #F5EDF0;
    --brun: #3D2B35;
    --taupe: #8C7A82;
    --blanc: #ffffff;
    --violet: #9B7FA6;
    --violet-light: #E8DFF0;
    --violet-med: #C4AED0;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', 'Nunito', sans-serif;
    background: var(--creme);
    color: var(--brun);
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
  }

  h1, h2, h3 { font-family: 'Cormorant Garamond', serif; }

  .script { font-family: 'Dancing Script', cursive; }

  /* ── LAYOUT ── */
  .app { min-height: 100vh; }

  /* ── HEADER ── */
  .header {
    background: rgba(253,248,249,0.96);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--rose-pale);
    padding: 12px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 2px 14px rgba(155,127,166,0.1);
  }
  .header-brand { display: flex; align-items: center; gap: 10px; }
  .header-crown { font-size: 18px; color: var(--violet); }
  .header-title { color: var(--violet); font-family: 'Cormorant Garamond', serif; font-size: 18px; font-weight: 600; letter-spacing: 1px; }
  .header-sub { color: var(--taupe); font-size: 10px; letter-spacing: 2px; text-transform: uppercase; }
  .header-day { background: var(--violet-light); border-radius: 20px; padding: 5px 13px; color: var(--violet); font-size: 11px; font-weight: 700; }

  /* ── BOTTOM NAV ── */
  .bottom-nav {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    background: rgba(253,248,249,0.97);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-top: 1px solid var(--rose-pale);
    display: flex;
    z-index: 200;
    padding-bottom: env(safe-area-inset-bottom);
    box-shadow: 0 -4px 20px rgba(155,127,166,0.1);
  }
  .bottom-nav-btn {
    flex: 1;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 3px;
    padding: 10px 4px;
    border: none; background: none;
    cursor: pointer;
    transition: all 0.2s;
  }
  .bottom-nav-icon { font-size: 20px; line-height: 1; }
  .bottom-nav-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.3px;
    color: var(--taupe);
    transition: color 0.2s;
  }
  .bottom-nav-btn.active .bottom-nav-label { color: var(--violet); }
  .bottom-nav-btn.active .bottom-nav-icon { filter: none; }
  .bottom-nav-dot {
    width: 4px; height: 4px; border-radius: 50%;
    background: var(--violet); margin-top: 1px;
  }

  /* ── PAGE ── */
  .page { padding: 20px 16px 100px; max-width: 600px; margin: 0 auto; animation: fadeIn 0.4s ease; }

  /* ── CARDS ── */
  .card {
    background: var(--blanc);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 2px 14px rgba(155,127,166,0.1);
    border: 1px solid var(--rose-pale);
  }
  .card-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--bordeaux);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .card-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--rose-poudre);
  }

  /* ── INPUTS ── */
  .field { margin-bottom: 14px; }
  .field label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: var(--rose-vieux);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .field input, .field textarea, .field select {
    width: 100%;
    border: 1px solid var(--rose-pale);
    border-radius: 10px;
    padding: 10px 14px;
    font-family: 'DM Sans', 'Nunito', sans-serif;
    font-size: 14px;
    color: var(--brun);
    background: var(--creme);
    outline: none;
    transition: border-color 0.2s;
    resize: none;
  }
  .field input:focus, .field textarea:focus {
    border-color: var(--rose-moyen);
    background: var(--blanc);
  }

  /* ── CHECKBOXES ── */
  .check-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .check-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--creme);
    border-radius: 10px;
    border: 1px solid var(--rose-pale);
    cursor: pointer;
    transition: all 0.2s;
  }
  .check-item.checked { background: var(--rose-poudre); border-color: var(--rose-vieux); }
  .check-item input[type="checkbox"] { display: none; }
  .check-box {
    width: 20px; height: 20px;
    border: 2px solid var(--rose-moyen);
    border-radius: 5px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
  }
  .check-item.checked .check-box {
    background: var(--rose-vieux);
    border-color: var(--rose-vieux);
    color: white;
    font-size: 12px;
  }
  .check-label { font-size: 13px; color: var(--brun); line-height: 1.3; }

  /* ── MOOD SELECTOR ── */
  .mood-row { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
  .mood-btn {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    padding: 10px 12px;
    border: 2px solid var(--rose-pale);
    border-radius: 12px;
    background: var(--creme);
    cursor: pointer;
    transition: all 0.2s;
    font-size: 22px;
    min-width: 56px;
  }
  .mood-btn.selected { border-color: var(--violet); background: var(--violet-light); }
  .mood-btn span { font-size: 10px; color: var(--taupe); font-family: 'Nunito', sans-serif; }
  .mood-btn.selected { border-color: var(--rose-vieux); background: var(--rose-poudre); }

  /* ── STARS ── */
  .stars { display: flex; gap: 6px; }
  .star { font-size: 24px; cursor: pointer; transition: transform 0.1s; color: var(--rose-pale); }
  .star.filled { color: var(--or); }
  .star:hover { transform: scale(1.2); }

  /* ── PROGRESS ── */
  .progress-bar {
    background: var(--rose-poudre);
    border-radius: 20px;
    height: 8px;
    overflow: hidden;
    margin: 8px 0;
  }
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--violet), var(--rose-vieux));
    border-radius: 20px;
    transition: width 0.5s ease;
  }

  /* ── BUTTONS ── */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: 50px;
    font-family: 'DM Sans', 'Nunito', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    cursor: pointer;
    transition: all 0.25s;
    text-decoration: none;
  }
  .btn-primary {
    background: linear-gradient(135deg, var(--violet), var(--rose-vieux));
    color: var(--blanc);
    box-shadow: 0 4px 18px rgba(155,127,166,0.35);
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(155,127,166,0.45); }
  .btn-secondary {
    background: var(--rose-poudre);
    color: var(--bordeaux);
    border: 1.5px solid var(--rose-pale);
  }
  .btn-gold {
    background: linear-gradient(135deg, var(--violet), var(--rose-vieux));
    color: var(--blanc);
    box-shadow: 0 4px 15px rgba(155,127,166,0.3);
  }
  .btn-block { width: 100%; }
  .btn-sm { padding: 8px 16px; font-size: 12px; }

  /* ── BADGES ── */
  .badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .badge-rose { background: var(--rose-poudre); color: var(--bordeaux); border-radius: 20px; }
  .badge-or { background: var(--rose-poudre); color: var(--rose-vieux); border: 1px solid var(--rose-pale); border-radius: 20px; }

  /* ── COVER ── */
  .cover {
    min-height: 100vh;
    background: linear-gradient(160deg, #9B7FA6 0%, #C4899A 45%, #8A6E9B 100%);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 24px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .cover::before { content:''; position:absolute; width:420px; height:420px; background:rgba(255,255,255,.06); border-radius:50%; top:-120px; right:-120px; }
  .cover::after { content:''; position:absolute; width:300px; height:300px; background:rgba(255,255,255,.04); border-radius:50%; bottom:-80px; left:-80px; }
  .cover-logo { font-size: 48px; margin-bottom: 8px; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }

  .cover-brand {
    font-family: 'Cormorant Garamond', serif;
    font-size: 13px; font-weight: 700; letter-spacing: 4px;
    color: var(--or); text-transform: uppercase; margin-bottom: 32px;
  }
  .cover-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 42px; font-weight: 300; line-height: 1.1;
    color: var(--bordeaux); margin-bottom: 8px;
  }
  .cover-script {
    font-family: 'Dancing Script', cursive;
    font-size: 28px; color: var(--rose-moyen); margin-bottom: 8px;
  }
  .cover-sub {
    font-size: 13px; color: rgba(255,255,255,0.88); margin-bottom: 40px; line-height: 1.6;
  }
  .cover-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, transparent, var(--or), transparent);
    margin: 20px auto;
  }
  .cover-input-section {
    width: 100%; max-width: 340px;
    background: var(--blanc);
    border-radius: 20px;
    padding: 24px;
    box-shadow: 0 8px 30px rgba(154,79,87,0.12);
    border: 1px solid var(--rose-pale);
    margin-bottom: 20px;
  }
  .cover-quote {
    font-family: 'Dancing Script', cursive;
    font-size: 16px; color: rgba(255,235,240,0.92);
    margin-top: 20px; line-height: 1.6;
    text-shadow: 0 1px 8px rgba(0,0,0,0.2);
  }

  /* ── PROFIL OPTIONS ── */
  .option-grid { display: flex; gap: 8px; flex-wrap: wrap; }
  .option-btn {
    padding: 8px 16px;
    border: 1.5px solid var(--rose-pale);
    border-radius: 50px;
    background: var(--creme);
    font-family: 'Nunito', sans-serif;
    font-size: 12px; font-weight: 600;
    color: var(--taupe);
    cursor: pointer;
    transition: all 0.2s;
  }
  .option-btn.selected {
    background: var(--rose-poudre);
    border-color: var(--rose-moyen);
    color: var(--bordeaux);
  }

  /* ── DAY HEADER ── */
  .day-header {
    background: linear-gradient(135deg, var(--bordeaux), var(--rose-vieux));
    border-radius: 16px;
    padding: 20px;
    color: var(--blanc);
    margin-bottom: 16px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .day-number { font-family: 'Cormorant Garamond', serif; font-size: 56px; font-weight: 300; color: rgba(255,255,255,0.3); line-height: 1; }
  .day-info { flex: 1; padding-left: 16px; }
  .day-label { font-size: 11px; letter-spacing: 2px; color: var(--rose-pale); text-transform: uppercase; }
  .day-date-input {
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 8px;
    padding: 6px 12px;
    color: var(--blanc);
    font-family: 'Nunito', sans-serif;
    font-size: 13px;
    width: 130px;
    outline: none;
  }

  /* ── WEEK HEADER ── */
  .week-header {
    background: var(--creme-fonce);
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 16px;
    border-left: 4px solid var(--or);
    display: flex; align-items: center; justify-content: space-between;
  }
  .week-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 32px; font-weight: 600; color: var(--bordeaux);
  }
  .week-label { font-size: 11px; letter-spacing: 2px; color: var(--taupe); text-transform: uppercase; }
  .week-quote { font-family: 'Dancing Script', cursive; font-size: 14px; color: var(--rose-moyen); text-align: right; max-width: 160px; }

  /* ── TRACKER GRID ── */
  .tracker-table { width: 100%; border-collapse: collapse; }
  .tracker-table th {
    padding: 8px 4px;
    font-size: 11px; font-weight: 700; letter-spacing: 1px;
    color: var(--bordeaux); text-align: center;
    border-bottom: 2px solid var(--rose-pale);
  }
  .tracker-table td { padding: 8px 4px; text-align: center; border-bottom: 1px solid var(--rose-poudre); }
  .tracker-table .gesture-name { text-align: left; font-size: 12px; color: var(--brun); padding-left: 0; }
  .tracker-check {
    width: 24px; height: 24px;
    border: 2px solid var(--rose-pale);
    border-radius: 6px;
    margin: 0 auto;
    cursor: pointer;
    transition: all 0.15s;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
  }
  .tracker-check.done { background: var(--rose-vieux); border-color: var(--rose-vieux); color: white; }

  /* ── COACH VIEW ── */
  .coach-login {
    min-height: 100vh;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 24px;
    background: var(--bordeaux);
  }
  .coach-card-list { display: flex; flex-direction: column; gap: 12px; }
  .client-card {
    background: var(--blanc);
    border-radius: 16px;
    padding: 16px 20px;
    display: flex; align-items: center; gap: 16px;
    border: 1px solid var(--rose-poudre);
    box-shadow: 0 2px 8px rgba(154,79,87,0.06);
  }
  .client-avatar {
    width: 44px; height: 44px;
    background: var(--rose-poudre);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; font-weight: 700; color: var(--bordeaux);
    font-family: 'Cormorant Garamond', serif;
    flex-shrink: 0;
  }
  .client-info { flex: 1; }
  .client-name { font-weight: 700; color: var(--bordeaux); font-size: 15px; }
  .client-stats { font-size: 12px; color: var(--taupe); margin-top: 2px; }
  .client-link { font-size: 11px; color: var(--rose-moyen); word-break: break-all; margin-top: 4px; }

  /* ── SERMENT ── */
  .serment-box {
    background: linear-gradient(135deg, var(--creme), var(--rose-poudre));
    border: 1px solid var(--rose-pale);
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    position: relative;
  }
  .serment-box::before {
    content: '';
    position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px;
    border: 1px solid var(--rose-pale);
    border-radius: 12px;
    pointer-events: none;
  }

  /* ── TOAST ── */
  .toast {
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    background: var(--bordeaux); color: var(--blanc);
    padding: 12px 24px; border-radius: 50px;
    font-size: 13px; font-weight: 600;
    z-index: 1000; animation: slideUp 0.3s ease;
    box-shadow: 0 4px 20px rgba(107,30,46,0.4);
  }
  @keyframes slideUp { from { opacity:0; transform: translateX(-50%) translateY(20px); } }

  /* ── DIVIDER ── */
  .or-divider {
    display: flex; align-items: center; gap: 12px;
    margin: 16px 0; color: var(--or); font-size: 14px;
  }
  .or-divider::before, .or-divider::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, transparent, var(--or), transparent);
  }

  /* ── SIGNATURE LINE ── */
  .sig-line {
    border-bottom: 1.5px solid var(--bordeaux);
    margin: 8px 0 4px;
    min-height: 32px;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 400px) {
    .check-grid { grid-template-columns: 1fr; }
    .mood-btn { min-width: 48px; padding: 8px; }
  }
`;

// ─── DATA ────────────────────────────────────────────────────────────────────
const MOODS = [
  { emoji: '😣', label: 'Difficile' },
  { emoji: '😕', label: 'Moyen' },
  { emoji: '😐', label: 'Neutre' },
  { emoji: '🙂', label: 'Bien' },
  { emoji: '😊', label: 'Epanouie' },
];

const WEEK_QUOTES = [
  "Une semaine à la fois. C'est comme ça que tout change.",
  "Plus je comprends mes cheveux, mieux je prends soin d'eux.",
  "La régularité d'aujourd'hui, les résultats de demain.",
  "Fière du chemin parcouru, et encore plus de celui qui continue.",
  "Tu n'es plus au début, tu es en train de devenir régulière.",
  "Ta couronne est en pleine transformation.",
];

const DAY_QUOTES = [
  "Je choisis moi, chaque jour.",
  "Chaque petit geste construit de grands résultats.",
  "La constance fait plus que la perfection.",
  "Mes cheveux méritent mon attention.",
  "Je suis disciplinée. Je suis constante. Je choisis ma couronne.",
  "Observer sans juger. Apprendre sans se comparer.",
  "Ma couronne se construit tous les jours.",
];

const HAIR_STATES = ['Sec', 'Cassant', 'Hydraté', 'Souple', 'Fragile', 'Coiffure protectrice'];

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function CheckItem({ label, checked, onChange }) {
  return (
    <div className={`check-item ${checked ? 'checked' : ''}`} onClick={() => onChange(!checked)}>
      <div className="check-box">{checked ? '✓' : ''}</div>
      <span className="check-label">{label}</span>
    </div>
  );
}

function MoodSelector({ value, onChange }) {
  return (
    <div className="mood-row">
      {MOODS.map((m, i) => (
        <button key={i} className={`mood-btn ${value === i ? 'selected' : ''}`}
          onClick={() => onChange(i)}>
          {m.emoji}
          <span>{m.label}</span>
        </button>
      ))}
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`star ${i <= value ? 'filled' : ''}`}
          onClick={() => onChange(i)}>★</span>
      ))}
    </div>
  );
}

function OptionPicker({ options, value, onChange, multi = false }) {
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (opt) => {
    if (multi) {
      const next = selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
      onChange(next);
    } else {
      onChange(value === opt ? '' : opt);
    }
  };
  return (
    <div className="option-grid">
      {options.map(opt => (
        <button key={opt}
          className={`option-btn ${(multi ? selected.includes(opt) : value === opt) ? 'selected' : ''}`}
          onClick={() => toggle(opt)}>
          {opt}
        </button>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, multiline, rows = 3, placeholder = '' }) {
  const [local, setLocal] = React.useState(value || '');
  // Sync si la valeur externe change (ex: chargement)
  React.useEffect(() => { setLocal(value || ''); }, [value]);
  return (
    <div className="field">
      {label && <label>{label}</label>}
      {multiline
        ? <textarea rows={rows} value={local}
            placeholder={placeholder}
            onChange={e => setLocal(e.target.value)}
            onBlur={e => { if (e.target.value !== value) onChange(e.target.value); }}
          />
        : <input type="text" value={local}
            placeholder={placeholder}
            onChange={e => setLocal(e.target.value)}
            onBlur={e => { if (e.target.value !== value) onChange(e.target.value); }}
          />
      }
    </div>
  );
}

function Toast({ msg }) {
  return msg ? <div className="toast">{msg}</div> : null;
}

// ─── WELCOME PAGE (après connexion) ──────────────────────────────────────────
function WelcomePage({ userName, onEnter, onGoToDay, daysCompleted, journalData, coachMessage }) {
  const progress = Math.round((daysCompleted / 42) * 100);

  // Trouver le prochain jour à remplir
  let nextDay = 1;
  for (let i = 1; i <= 42; i++) {
    const d = journalData[`day-${i}`];
    const done = d && (d.rituel?.length > 0 || d.humeur !== undefined);
    if (!done) { nextDay = i; break; }
    if (i === 42) nextDay = 42;
  }

  const semActuelle = Math.min(6, Math.ceil(nextDay / 7));

  const messages = [
    "Ta couronne se construit tous les jours. 🌿",
    "Chaque petit geste compte.",
    "La régularité fait plus que la perfection.",
    "Comprendre tes cheveux, c'est reprendre le pouvoir.",
    "Tu es sur le bon chemin. Continue.",
  ];
  const msg = messages[new Date().getDay() % messages.length];

  const phaseLabel = daysCompleted === 0
    ? "C'est le moment de commencer ✨"
    : daysCompleted < 7 ? `Semaine 1 en cours — continue !`
    : daysCompleted < 14 ? "Semaine 2 — tu prends le rythme !"
    : daysCompleted < 21 ? "Semaine 3 — mi-parcours, bravo !"
    : daysCompleted < 28 ? "Semaine 4 — la régularité s'installe !"
    : daysCompleted < 35 ? "Semaine 5 — tu es en transformation !"
    : daysCompleted < 42 ? "Semaine 6 — la ligne d'arrivée !"
    : "42 jours accomplis ! Tu es incroyable ! 🏆";

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, var(--creme) 0%, var(--rose-poudre) 55%, var(--creme) 100%)',
      display: 'flex', flexDirection: 'column',
      padding: '0 0 40px',
    }}>

      {/* Header */}
      <div style={{
        background: 'rgba(253,248,249,0.96)', backdropFilter: 'blur(10px)',
        borderBottom: '1px solid var(--rose-pale)',
        padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img
            src={process.env.PUBLIC_URL + '/logo_header.png'}
            alt="Missawoa"
            style={{ height: '36px', width: 'auto', borderRadius: '50%' }}
          />
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '16px', fontWeight: '600', color: 'var(--violet)' }}>
            Missawoa
          </div>
        </div>
        <button onClick={onEnter}
          style={{ background: 'none', border: 'none', color: 'var(--taupe)', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
          Sommaire →
        </button>
      </div>

      {/* Message coach */}
      {coachMessage && (
        <div style={{
          background: 'linear-gradient(135deg, var(--bordeaux), var(--rose-vieux))',
          padding: '14px 20px', display: 'flex', gap: '12px', alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>👑</span>
          <div>
            <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--rose-pale)', letterSpacing: '2px', marginBottom: '4px' }}>
              MESSAGE DE TA COACH
            </div>
            <div style={{ fontSize: '13px', color: 'white', lineHeight: '1.6', fontStyle: 'italic' }}>
              {coachMessage}
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '24px 20px 0', maxWidth: '520px', margin: '0 auto', width: '100%' }}>

        {/* Salutation */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--taupe)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <div style={{ marginBottom: '6px' }}>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '26px', fontWeight: '300', color: 'var(--bordeaux)', lineHeight: '1.2' }}>
              Bonjour,<br/><strong style={{ fontWeight: '600' }}>{userName}</strong> 🌿
            </div>
          </div>
          <div style={{ fontFamily: 'Dancing Script, cursive', fontSize: '15px', color: 'var(--rose-moyen)', lineHeight: '1.6' }}>
            "{msg}"
          </div>
        </div>

        {/* Carte progression */}
        <div style={{
          background: 'var(--blanc)', borderRadius: '16px', padding: '18px 20px',
          border: '1px solid var(--rose-pale)', boxShadow: '0 3px 16px rgba(155,127,166,0.1)',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--taupe)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Ma progression
            </div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: '600', color: 'var(--bordeaux)' }}>
              {daysCompleted}<span style={{ fontSize: '14px', fontWeight: '300', color: 'var(--taupe)' }}>/42</span>
            </div>
          </div>

          {/* Barre de progression */}
          <div style={{ background: 'var(--rose-poudre)', borderRadius: '20px', height: '8px', overflow: 'hidden', marginBottom: '6px' }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: 'linear-gradient(90deg, var(--violet), var(--rose-vieux))',
              borderRadius: '20px', transition: 'width 1s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--rose-vieux)', fontWeight: '600', fontStyle: 'italic' }}>
              {phaseLabel}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--taupe)', fontWeight: '600' }}>{progress}%</div>
          </div>

          {/* Mini grille 6 semaines */}
          {daysCompleted > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px', marginTop: '14px' }}>
              {[1,2,3,4,5,6].map(sem => {
                const joursS = Array.from({length:7},(_,i) => (sem-1)*7+i+1);
                const faits = joursS.filter(j => {
                  const d = journalData[`day-${j}`];
                  return d && (d.rituel?.length > 0 || d.humeur !== undefined);
                }).length;
                const pct = Math.round((faits/7)*100);
                const active = sem === semActuelle;
                return (
                  <div key={sem} style={{ textAlign: 'center' }}>
                    <div style={{
                      height: '4px', borderRadius: '4px', marginBottom: '4px',
                      background: pct === 100 ? 'var(--rose-vieux)' : active ? 'var(--violet-med)' : 'var(--rose-poudre)',
                    }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: 'linear-gradient(90deg, var(--violet), var(--rose-vieux))',
                        borderRadius: '4px'
                      }} />
                    </div>
                    <div style={{ fontSize: '9px', color: active ? 'var(--bordeaux)' : 'var(--taupe)', fontWeight: active ? '700' : '400' }}>
                      S{sem}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bouton principal — Semaine 1 si débutante, sinon jour suivant */}
        <button
          onClick={() => {
            if (daysCompleted === 42) {
              onEnter('bilan');
            } else if (daysCompleted === 0) {
              onEnter('semaine-1'); // Toujours commencer par la page Semaine 1
            } else if (nextDay % 7 === 1) {
              onEnter('semaine-' + Math.ceil(nextDay / 7)); // Début d'une semaine → page semaine
            } else {
              onGoToDay(nextDay);
            }
          }}
          style={{
            width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
            background: 'linear-gradient(135deg, var(--violet), var(--rose-vieux))',
            color: 'white', cursor: 'pointer',
            boxShadow: '0 6px 24px rgba(155,127,166,0.4)',
            marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', opacity: '0.8', marginBottom: '4px' }}>
              {daysCompleted === 0 ? 'Commencer' : 'Continuer'}
            </div>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: '600', lineHeight: 1 }}>
              {daysCompleted === 42 ? 'Voir mon bilan'
                : daysCompleted === 0 ? 'Semaine 1'
                : nextDay % 7 === 1 ? 'Semaine ' + Math.ceil(nextDay / 7)
                : 'Jour ' + nextDay}
            </div>
          </div>
          <div style={{ fontSize: '28px', opacity: '0.9' }}>→</div>
        </button>

        {/* Tableau progression soins — 6 soins sur 6 semaines */}
        <div style={{
          background: 'var(--blanc)', borderRadius: '16px', padding: '16px 18px',
          border: '1px solid var(--rose-pale)', boxShadow: '0 3px 16px rgba(155,127,166,0.08)',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--taupe)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              🧴 Mes soins capillaires
            </div>
            <div style={{ fontSize: '11px', color: 'var(--rose-vieux)', fontWeight: '700' }}>
              {[1,2,3,4,5,6].filter(s => {
                const sData = journalData['semaine-' + s];
                return sData?.soins && Object.values(sData.soins).some(v => v.fait);
              }).length}/6
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
            {[1,2,3,4,5,6].map(sem => {
              const sData = journalData['semaine-' + sem];
              const soins = sData?.soins || {};
              const nbFaits = Object.values(soins).filter(v => v.fait).length;
              const total = 5;
              const complet = nbFaits === total;
              const partiel = nbFaits > 0 && nbFaits < total;
              return (
                <div key={sem} style={{ textAlign: 'center' }}>
                  <div style={{
                    height: '36px', borderRadius: '8px',
                    background: complet ? 'var(--rose-vieux)' : partiel ? 'var(--rose-pale)' : 'var(--rose-poudre)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid ' + (complet ? 'var(--rose-vieux)' : partiel ? 'var(--rose-moyen)' : 'var(--rose-pale)'),
                    marginBottom: '4px', cursor: 'pointer',
                  }}>
                    {complet
                      ? <span style={{ fontSize: '14px' }}>✓</span>
                      : partiel
                        ? <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--bordeaux)' }}>{nbFaits}/{total}</span>
                        : <span style={{ fontSize: '10px', color: 'var(--taupe)' }}>—</span>
                    }
                  </div>
                  <div style={{
                    fontSize: '9px', fontWeight: '600',
                    color: complet ? 'var(--bordeaux)' : 'var(--taupe)',
                    letterSpacing: '0.5px',
                  }}>S{sem}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px', fontSize: '10px', color: 'var(--taupe)' }}>
            <span>✓ = soin complet</span>
            <span style={{ color: 'var(--rose-vieux)' }}>x/5 = en cours</span>
            <span>— = à faire</span>
          </div>
        </div>

        {/* Accès rapides */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
          {[
            { label: 'Mon profil', icon: '👤', id: 'profil' },
            { label: 'Mes gestes', icon: '🌿', id: 'engagements' },
            { label: 'Sommaire', icon: '🗂️', id: 'sommaire' },
            { label: 'FAQ', icon: '❓', id: 'faq' },
          ].map(item => (
            <button key={item.id} onClick={() => onEnter(item.id)}
              style={{
                background: 'var(--blanc)', border: '1px solid var(--rose-pale)',
                borderRadius: '12px', padding: '12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'left',
              }}>
              <span style={{ fontSize: '18px' }}>{item.icon}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--bordeaux)', fontFamily: 'DM Sans, sans-serif' }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>

        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--taupe)' }}>
          Coaching capillaire · Gabon · @missawoa
        </div>
      </div>
    </div>
  );
}

// ─── COVER / LOGIN ────────────────────────────────────────────────────────────
function Cover({ onStart, existingUser }) {
  const [name, setName] = useState('');
  return (
    <div className="cover">
      <img
        src={process.env.PUBLIC_URL + '/logo_full_cover.png'}
        alt="Missawoa - Coach Capillaire"
        style={{ width: '240px', maxWidth: '70vw', marginBottom: '16px', filter: 'drop-shadow(0 8px 24px rgba(155,127,166,0.3))' }}
      />
      <div className="cover-sub" style={{ marginTop: '4px' }}>
        42 jours pour apprendre<br/>à connaître tes cheveux
      </div>

      <div className="cover-input-section">
        {existingUser ? (
          <>
            <p style={{fontSize:'14px', color:'var(--taupe)', marginBottom:'16px', textAlign:'center'}}>
              Bon retour, <strong style={{color:'var(--bordeaux)'}}>{existingUser}</strong> 🌿
            </p>
            <button className="btn btn-primary btn-block" onClick={() => onStart(existingUser)}>
              Reprendre mon journal
            </button>
          </>
        ) : (
          <>
            <Field label="Mon prénom" value={name} onChange={setName} placeholder="Écris ton prénom..." />
            <button className="btn btn-primary btn-block"
              onClick={() => name.trim() && onStart(name.trim())}
              style={{marginTop:'8px'}}>
              Commencer mon voyage ✨
            </button>
          </>
        )}
      </div>
      <div className="cover-quote">
        "Comprendre tes cheveux,<br/>c'est reprendre le pouvoir."
      </div>
    </div>
  );
}

// ─── PROFIL PAGE ──────────────────────────────────────────────────────────────
function ProfilPage({ data, onChange }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="page">
      <div className="card">
        <div className="card-title">👤 Mon profil capillaire</div>
        <Field label="Objectif principal ce mois-ci"
          value={data.objectif} onChange={v => update('objectif', v)}
          multiline rows={2} placeholder="En une phrase, qu'est-ce que je veux voir changer ?" />
      </div>

      <div className="card">
        <div className="card-title">💧 Type & caractéristiques</div>
        <div className="field">
          <label>Type de cheveux</label>
          <OptionPicker options={['Crépus (naturels)', 'Défrisés', 'En transition']}
            value={data.type} onChange={v => update('type', v)} />
        </div>
        <div className="field">
          <label>Épaisseur du cheveu</label>
          <OptionPicker options={['Fin', 'Moyen', 'Épais']}
            value={data.epaisseur} onChange={v => update('epaisseur', v)} />
        </div>
        <div className="field">
          <label>Porosité</label>
          <OptionPicker options={['Faible', 'Normale', 'Forte']}
            value={data.porosite} onChange={v => update('porosite', v)} />
        </div>
        <div className="field">
          <label>Densité</label>
          <OptionPicker options={['Fine', 'Moyenne', 'Dense']}
            value={data.densite} onChange={v => update('densite', v)} />
        </div>
        <div className="field">
          <label>Élasticité</label>
          <OptionPicker options={['Faible', 'Moyenne', 'Bonne']}
            value={data.elasticite} onChange={v => update('elasticite', v)} />
        </div>
        <div className="field">
          <label>Chute</label>
          <OptionPicker options={['Peu', 'Modérée', 'Importante']}
            value={data.chute} onChange={v => update('chute', v)} />
        </div>
      </div>

      <div className="card">
        <div className="card-title">⚠️ Mes problèmes principaux</div>
        <div className="check-grid">
          {['Sécheresse / manque d\'hydratation', 'Casse / fragilité',
            'Chute excessive', 'Croissance lente / stagnation',
            'Pellicules / cuir chevelu irrité', 'Autre'].map(p => (
            <CheckItem key={p} label={p}
              checked={(data.problemes || []).includes(p)}
              onChange={checked => {
                const curr = data.problemes || [];
                update('problemes', checked ? [...curr, p] : curr.filter(x => x !== p));
              }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ENGAGEMENTS PAGE ─────────────────────────────────────────────────────────
function EngagementsPage({ data, onChange }) {
  const update = (i, key, val) => {
    const gestes = [...(data.gestes || [{},{},{}])];
    gestes[i] = { ...gestes[i], [key]: val };
    onChange({ ...data, gestes });
  };
  const gestes = data.gestes || [{},{},{}];
  const titres = ['Prendre soin', 'Routine & Régularité', 'Connexion & Écoute'];

  return (
    <div className="page">
      <div style={{textAlign:'center', marginBottom:'20px'}}>
        <div className="cover-logo" style={{fontSize:'32px'}}>👑</div>
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'22px', color:'var(--bordeaux)', marginTop:'8px'}}>
          Mes 3 Engagements
        </div>
        <p style={{fontSize:'13px', color:'var(--taupe)', marginTop:'8px', lineHeight:'1.5'}}>
          Définis lors de ton diagnostic Missawoa.<br/>Personnalisés pour TES cheveux.
        </p>
      </div>

      {gestes.map((g, i) => (
        <div className="card" key={i} style={{borderLeft:`4px solid var(--${i===0?'bordeaux':i===1?'rose-vieux':'rose-moyen'})`}}>
          <div className="card-title">
            <span style={{
              background: i===0 ? 'var(--bordeaux)' : i===1 ? 'var(--rose-vieux)' : 'var(--rose-moyen)',
              color: 'white', width:'28px', height:'28px', borderRadius:'50%',
              display:'inline-flex', alignItems:'center', justifyContent:'center',
              fontSize:'14px', fontWeight:'700', flexShrink:0
            }}>{i+1}</span>
            Geste {i+1} — {titres[i]}
          </div>
          <Field label="Quel geste ?" value={g.quoi} onChange={v => update(i, 'quoi', v)} placeholder="Décris ton geste..." />
          <Field label="Quand et à quelle fréquence ?" value={g.quand} onChange={v => update(i, 'quand', v)} placeholder="Ex: Tous les soirs, 2x par semaine..." />
          <Field label="Pourquoi c'est important pour moi ?" value={g.pourquoi} onChange={v => update(i, 'pourquoi', v)} multiline rows={2} />
        </div>
      ))}

      <div style={{textAlign:'center', padding:'16px', color:'var(--taupe)', fontSize:'12px', fontStyle:'italic'}}>
        💡 Prends une photo de cette page et mets-la en fond d'écran
      </div>
    </div>
  );
}

// ─── SERMENT PAGE ─────────────────────────────────────────────────────────────
function SermentPage({ data, onChange, userName }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="page">
      <div className="serment-box" style={{marginBottom:'16px'}}>
        <img
          src={process.env.PUBLIC_URL + '/logo_header.png'}
          alt="Missawoa"
          style={{ height: '64px', width: 'auto', marginBottom: '8px', filter: 'drop-shadow(0 4px 12px rgba(155,127,166,0.2))' }}
        />
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'24px', color:'var(--bordeaux)', marginBottom:'4px'}}>
          Le Serment de la Couronne
        </div>
        <div style={{fontFamily:'Dancing Script, cursive', fontSize:'14px', color:'var(--rose-moyen)', marginBottom:'16px'}}>
          Missawoa
        </div>

        <div style={{background:'var(--blanc)', borderRadius:'12px', padding:'16px', marginBottom:'16px', textAlign:'left'}}>
          <p style={{fontSize:'13px', color:'var(--brun)', lineHeight:'1.7', fontStyle:'italic'}}>
            Moi, <strong>{userName}</strong>, reconnaissant que mes cheveux méritent la même attention que je donne au reste de mon corps, je m'engage aujourd'hui à entamer ce voyage de 6 semaines vers la compréhension et la santé de ma fibre capillaire.
          </p>
        </div>

        {[
          { num:'1', titre:'Écoute & Observation', texte:"Je m'engage à observer mes cheveux sans juger et à apprendre sans me comparer." },
          { num:'2', titre:'Action & Constance', texte:"Je m'engage à appliquer mes 3 gestes personnalisés avec régularité, car je sais que chaque petit geste construit de grands résultats." },
          { num:'3', titre:'Bienveillance', texte:"Je m'engage à être patiente. Si je manque un jour, je ne culpabiliserai pas, car la constance fait plus que la perfection." },
        ].map(e => (
          <div key={e.num} style={{display:'flex', gap:'12px', marginBottom:'12px', textAlign:'left', background:'var(--creme)', borderRadius:'10px', padding:'12px'}}>
            <div style={{background:'var(--bordeaux)', color:'white', width:'24px', height:'24px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:'700', flexShrink:0}}>
              {e.num}
            </div>
            <div>
              <div style={{fontWeight:'700', fontSize:'12px', color:'var(--bordeaux)', marginBottom:'4px'}}>{e.titre}</div>
              <div style={{fontSize:'12px', color:'var(--brun)', lineHeight:'1.5'}}>{e.texte}</div>
            </div>
          </div>
        ))}

        <div className="or-divider">♥</div>
        <div style={{fontFamily:'Dancing Script, cursive', fontSize:'16px', color:'var(--bordeaux)', marginBottom:'20px'}}>
          "Comprendre mes cheveux, c'est reprendre mon pouvoir."
        </div>

        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', textAlign:'left'}}>
          <div>
            <div style={{fontSize:'10px', fontWeight:'700', color:'var(--taupe)', letterSpacing:'1px', marginBottom:'6px'}}>SIGNATURE DE LA REINE</div>
            <Field value={data.signatureCliente} onChange={v => update('signatureCliente', v)} placeholder="Ta signature..." />
            <Field label="Date" value={data.dateSerment} onChange={v => update('dateSerment', v)} placeholder="__ / __ / ____" />
          </div>
          <div>
            <div style={{fontSize:'10px', fontWeight:'700', color:'var(--taupe)', letterSpacing:'1px', marginBottom:'6px'}}>COACH MISSAWOA</div>
            <div className="sig-line" />
            <div style={{fontSize:'11px', color:'var(--taupe)', marginTop:'4px'}}>Coach Missawoa</div>
          </div>
        </div>
      </div>

      <div style={{textAlign:'center', padding:'12px', background:'var(--bordeaux)', borderRadius:'12px', color:'var(--blanc)'}}>
        <div style={{fontSize:'10px', letterSpacing:'2px', color:'var(--rose-pale)', marginBottom:'4px'}}>CERTIFIÉ MISSAWOA</div>
        <div style={{fontFamily:'Dancing Script, cursive', fontSize:'14px', color:'var(--or-clair)'}}>Coach capillaire · Gabon</div>
      </div>
    </div>
  );
}

// ─── WEEK TRACKER ─────────────────────────────────────────────────────────────
function WeekPage({ weekNum, data, onChange, gestes, onNavigate }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  const premierJour = (weekNum - 1) * 7 + 1;
  const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  // Labels dynamiques : vrais gestes personnalisés de la cliente
  const gestesLabels = [
    gestes[0]?.quoi ? (gestes[0].quoi.length > 22 ? gestes[0].quoi.slice(0,22)+'…' : gestes[0].quoi) : 'Geste 1 (à définir)',
    gestes[1]?.quoi ? (gestes[1].quoi.length > 22 ? gestes[1].quoi.slice(0,22)+'…' : gestes[1].quoi) : 'Geste 2 (à définir)',
    gestes[2]?.quoi ? (gestes[2].quoi.length > 22 ? gestes[2].quoi.slice(0,22)+'…' : gestes[2].quoi) : 'Geste 3 (à définir)',
    'Hydratation (eau)',
    'Geste positif pour moi',
  ];

  const toggleCheck = (geste, day) => {
    const grid = { ...(data.grid || {}) };
    const key = `${geste}-${day}`;
    grid[key] = !grid[key];
    update('grid', grid);
  };

  return (
    <div className="page">
      <div className="week-header">
        <div>
          <div className="week-label">Semaine</div>
          <div className="week-num">0{weekNum}</div>
        </div>
        <div className="week-quote">{WEEK_QUOTES[weekNum-1]}</div>
      </div>

      <div className="card">
        <div className="card-title">📅 Mon suivi de la semaine</div>
        <div style={{overflowX:'auto'}}>
          <table className="tracker-table">
            <thead>
              <tr>
                <th style={{textAlign:'left', minWidth:'100px'}}>Geste</th>
                {days.map(d => <th key={d}>{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {gestesLabels.map((g, gi) => (
                <tr key={gi}>
                  <td className="gesture-name" style={{fontSize:'11px', maxWidth:'100px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {g.length > 18 ? g.slice(0, 18) + '…' : g}
                  </td>
                  {days.map((d, di) => (
                    <td key={di}>
                      <div className={`tracker-check ${(data.grid || {})[`${gi}-${di}`] ? 'done' : ''}`}
                        onClick={() => toggleCheck(gi, di)}>
                        {(data.grid || {})[`${gi}-${di}`] ? '✓' : ''}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>🎯 Mon intention</div>
          <Field value={data.intention} onChange={v => update('intention', v)}
            multiline rows={3} placeholder="Cette semaine, je décide de..." />
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>🌿 Mes produits</div>
          {[1,2,3].map(i => (
            <Field key={i} value={(data.produits || {})[i]}
              onChange={v => update('produits', {...(data.produits||{}), [i]:v})}
              placeholder={`Produit ${i}...`} />
          ))}
        </div>
      </div>

      {/* ── Section soins de la semaine ── */}
      <div className="card">
        <div className="card-title">🧴 Mon soin de la semaine</div>
        <p style={{fontSize:'12px', color:'var(--taupe)', marginBottom:'14px', lineHeight:'1.6'}}>
          Note tes soins capillaires de cette semaine — type et produit utilisé.
        </p>

        {/* Bain d'huile */}
        {[
          { key:"bain_huile", label:"Bain d'huile", type: null },
          { key:'shampoing', label:'Shampoing', type: ['Doux', 'Clarifiant'] },
          { key:'masque', label:'Masque', type: ['Hydratant', 'Protéiné'] },
          { key:'lait', label:'Lait capillaire', type: null },
          { key:'huile_scellage', label:'Huile de scellage', type: null },
        ].map(soin => {
          const s = (data.soins || {})[soin.key] || {};
          const updateSoin = (field, val) => {
            const soins = { ...(data.soins || {}) };
            soins[soin.key] = { ...(soins[soin.key] || {}), [field]: val };
            update('soins', soins);
          };
          return (
            <div key={soin.key} style={{
              padding: '12px 0',
              borderBottom: '1px solid var(--rose-poudre)',
            }}>
              {/* Ligne titre + case à cocher */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: soin.type || !s.fait ? '8px' : '0' }}>
                <div
                  onClick={() => updateSoin('fait', !s.fait)}
                  style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0, cursor: 'pointer',
                    border: `2px solid ${s.fait ? 'var(--rose-vieux)' : 'var(--rose-pale)'}`,
                    background: s.fait ? 'var(--rose-vieux)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '12px', fontWeight: '700',
                    transition: 'all 0.2s',
                  }}>
                  {s.fait ? '✓' : ''}
                </div>
                <span style={{
                  fontSize: '13px', fontWeight: '600',
                  color: s.fait ? 'var(--bordeaux)' : 'var(--brun)',
                  textDecoration: 'none',
                }}>
                  {soin.label}
                </span>
              </div>

              {/* Choix type (Doux/Clarifiant ou Hydratant/Protéiné) */}
              {soin.type && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', paddingLeft: '32px' }}>
                  {soin.type.map(t => (
                    <button key={t}
                      onClick={() => updateSoin('type', s.type === t ? '' : t)}
                      style={{
                        padding: '4px 12px', borderRadius: '20px', cursor: 'pointer',
                        border: `1.5px solid ${s.type === t ? 'var(--rose-vieux)' : 'var(--rose-pale)'}`,
                        background: s.type === t ? 'var(--rose-poudre)' : 'transparent',
                        fontSize: '11px', fontWeight: '600',
                        color: s.type === t ? 'var(--bordeaux)' : 'var(--taupe)',
                        fontFamily: 'DM Sans, sans-serif',
                      }}>
                      {t}
                    </button>
                  ))}
                </div>
              )}

              {/* Champ produit utilisé */}
              <div style={{ paddingLeft: '32px' }}>
                <input
                  type="text"
                  value={s.produit || ''}
                  onChange={e => updateSoin('produit', e.target.value)}
                  placeholder="Produit utilisé..."
                  style={{
                    width: '100%', border: '1px solid var(--rose-pale)',
                    borderRadius: '8px', padding: '7px 12px',
                    fontSize: '13px', color: 'var(--brun)',
                    background: 'var(--creme)', fontFamily: 'DM Sans, sans-serif',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-title">🏆 Ma victoire de la semaine</div>
        <Field value={data.victoire} onChange={v => update('victoire', v)}
          placeholder="Ma plus grande fierté cette semaine..." />
      </div>

      {/* ── Bouton démarrer le premier jour de la semaine ── */}
      {onNavigate && (
        <button onClick={() => onNavigate('day-' + premierJour)}
          style={{
            width: '100%', padding: '15px', borderRadius: '50px', border: 'none',
            background: 'linear-gradient(135deg, var(--violet), var(--rose-vieux))',
            color: 'white', fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px', fontWeight: '700', cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(155,127,166,0.4)',
            marginTop: '4px',
          }}>
          Commencer le jour {premierJour} →
        </button>
      )}
    </div>
  );
}

// ─── DAY PAGE ─────────────────────────────────────────────────────────────────
function DayPage({ dayNum, data, onChange, gestes, onNavigate }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  const [showCelebration, setShowCelebration] = React.useState(false);
  const [celebrationMsg, setCelebrationMsg] = React.useState('');

  const markComplete = () => {
    const estFinSemaine = dayNum % 7 === 0;
    if (estFinSemaine) {
      setCelebrationMsg(`🎉 Semaine ${Math.ceil(dayNum/7)} terminée ! Tu es incroyable.`);
    } else {
      setCelebrationMsg(`🌿 Jour ${dayNum} complété ! Continue comme ça.`);
    }
    update('complete', true);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 3500);
  };

  return (
    <div className="page">
      <div className="day-header">
        <div className="day-number">{String(dayNum).padStart(2,'0')}</div>
        <div className="day-info">
          <div className="day-label">Jour</div>
          <input className="day-date-input" type="date"
            value={data.date || new Date().toISOString().split('T')[0]}
            onChange={e => {
              update('date', e.target.value);
            }} />
          <div style={{marginTop:'8px', display:'flex', gap:'6px'}}>
            {['L','M','M','J','V','S','D'].map((d,i) => {
              // Calcul auto depuis la date saisie (lundi=0 … dimanche=6)
              const dateVal = data.date || new Date().toISOString().split('T')[0];
              const jourAuto = dateVal
                ? (new Date(dateVal + 'T12:00:00').getDay() + 6) % 7
                : null;
              const actif = jourAuto !== null ? jourAuto === i : data.jourSemaine === i;
              return (
                <span key={i}
                  style={{
                    width:'22px', height:'22px', borderRadius:'50%', display:'inline-flex',
                    alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:'700',
                    background: actif ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                    color: actif ? 'var(--bordeaux)' : 'rgba(255,255,255,0.7)',
                  }}>{d}</span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">✨ Mon rituel du jour</div>
        <div className="check-grid">
          {[
            gestes[0]?.quoi || 'Mon geste 1 (définis-le dans Gestes)',
            gestes[1]?.quoi || 'Mon geste 2 (définis-le dans Gestes)',
            gestes[2]?.quoi || 'Mon geste 3 (définis-le dans Gestes)',
            'Hydratation (eau)',
            'Protection',
            'Geste positif pour moi',
          ].map(g => (
            <CheckItem key={g} label={g}
              checked={(data.rituel || []).includes(g)}
              onChange={checked => {
                const curr = data.rituel || [];
                update('rituel', checked ? [...curr, g] : curr.filter(x => x !== g));
              }} />
          ))}
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>💧 Mes cheveux</div>
          <div style={{marginBottom:'10px'}}>
            <label style={{fontSize:'10px', fontWeight:'700', color:'var(--rose-vieux)', letterSpacing:'1px', textTransform:'uppercase', display:'block', marginBottom:'6px'}}>État du jour</label>
            <OptionPicker options={HAIR_STATES} value={data.etatCheveux || []}
              onChange={v => update('etatCheveux', v)} multi />
          </div>
          <Field label="Notes / Observations" value={data.notesCheveux}
            onChange={v => update('notesCheveux', v)} multiline rows={3} />
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>💫 Comment je me sens</div>
          <div style={{marginBottom:'12px'}}>
            <label style={{fontSize:'10px', fontWeight:'700', color:'var(--rose-vieux)', letterSpacing:'1px', textTransform:'uppercase', display:'block', marginBottom:'8px'}}>Humeur</label>
            <MoodSelector value={data.humeur} onChange={v => update('humeur', v)} />
          </div>
          <div style={{marginBottom:'10px'}}>
            <label style={{fontSize:'10px', fontWeight:'700', color:'var(--rose-vieux)', letterSpacing:'1px', textTransform:'uppercase', display:'block', marginBottom:'6px'}}>Énergie</label>
            <Stars value={data.energie || 0} onChange={v => update('energie', v)} />
          </div>
          <Field label="Produit utilisé" value={data.produit}
            onChange={v => update('produit', v)} placeholder="Nom du produit..." />
        </div>
      </div>

      <div className="card">
        <div className="card-title">🌱 Intention & Victoire</div>
        <Field label="Ce qui m'a fait du bien aujourd'hui"
          value={data.bienEtre} onChange={v => update('bienEtre', v)}
          placeholder="Un geste, une pensée, une sensation..." />
        <Field label="Ma petite victoire du jour"
          value={data.victoire} onChange={v => update('victoire', v)}
          placeholder="Même petite, elle compte..." />
        <Field label="Ma pensée positive"
          value={data.pensee} onChange={v => update('pensee', v)} multiline rows={2} />
      </div>

      <div style={{textAlign:'center', padding:'12px 0', fontFamily:'Dancing Script, cursive', fontSize:'16px', color:'var(--rose-moyen)'}}>
        "{DAY_QUOTES[dayNum % DAY_QUOTES.length]}"
      </div>

      {/* ── Bouton marquer complété ── */}
      {!data.complete && (
        <button onClick={markComplete}
          style={{
            width: '100%', padding: '14px', borderRadius: '50px', border: 'none',
            background: 'linear-gradient(135deg, var(--violet), var(--rose-vieux))',
            color: 'white', fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px', fontWeight: '700', cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(155,127,166,0.4)',
            marginBottom: '12px',
          }}>
          ✓ Marquer ce jour comme complété
        </button>
      )}
      {data.complete && (
        <div style={{
          textAlign: 'center', padding: '12px',
          background: 'var(--rose-poudre)', borderRadius: '50px',
          color: 'var(--bordeaux)', fontSize: '13px', fontWeight: '700',
          marginBottom: '12px', border: '1.5px solid var(--rose-pale)'
        }}>
          ✓ Jour {dayNum} complété 🌿
        </div>
      )}

      {/* ── Overlay célébration ── */}
      {showCelebration && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(155,127,166,0.25)', backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', padding: '32px 28px',
            textAlign: 'center', maxWidth: '300px', width: '90%',
            boxShadow: '0 20px 60px rgba(155,127,166,0.3)',
            border: '1px solid var(--rose-pale)',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              {dayNum % 7 === 0 ? '🏆' : '🌿'}
            </div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif', fontSize: '22px',
              color: 'var(--bordeaux)', marginBottom: '8px', fontWeight: '600'
            }}>
              {dayNum % 7 === 0 ? 'Semaine complète !' : 'Bravo !'}
            </div>
            <div style={{
              fontFamily: 'Dancing Script, cursive', fontSize: '16px',
              color: 'var(--rose-moyen)', lineHeight: 1.5
            }}>
              {celebrationMsg}
            </div>
          </div>
        </div>
      )}

      {/* ── Navigation jour précédent / suivant ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: '8px', gap: '10px'
      }}>
        {dayNum > 1 ? (
          <button onClick={() => onNavigate('day-' + (dayNum - 1))}
            style={{
              flex: 1, padding: '13px 16px', borderRadius: '50px',
              border: '1.5px solid var(--rose-pale)', background: 'var(--creme)',
              color: 'var(--bordeaux)', fontFamily: 'DM Sans, sans-serif',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center'
            }}>
            ← Jour {dayNum - 1}
          </button>
        ) : <div style={{flex:1}} />}

        {dayNum < 42 ? (
          dayNum % 7 === 0 ? (
            <button onClick={() => onNavigate('semaine-' + (Math.ceil(dayNum / 7) + 1))}
              style={{
                flex: 1, padding: '13px 16px', borderRadius: '50px', border: 'none',
                background: 'linear-gradient(135deg, var(--bordeaux), var(--violet))',
                color: 'white', fontFamily: 'DM Sans, sans-serif',
                fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(155,127,166,0.4)',
                display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center'
              }}>
              {'Démarrer la semaine ' + (Math.ceil(dayNum / 7) + 1) + ' ✨'}
            </button>
          ) : (
            <button onClick={() => onNavigate('day-' + (dayNum + 1))}
              style={{
                flex: 1, padding: '13px 16px', borderRadius: '50px', border: 'none',
                background: 'linear-gradient(135deg, var(--violet), var(--rose-vieux))',
                color: 'white', fontFamily: 'DM Sans, sans-serif',
                fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(155,127,166,0.35)',
                display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center'
              }}>
              {'Jour ' + (dayNum + 1) + ' →'}
            </button>
          )
        ) : (
          <button onClick={() => onNavigate('bilan')}
            style={{
              flex: 1, padding: '13px 16px', borderRadius: '50px', border: 'none',
              background: 'linear-gradient(135deg, var(--bordeaux), var(--rose-vieux))',
              color: 'white', fontFamily: 'DM Sans, sans-serif',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(155,127,166,0.35)',
            }}>
            Voir mon bilan 🏆
          </button>
        )}
      </div>
    </div>
  );
}

// ─── SOMMAIRE PAGE ───────────────────────────────────────────────────────────
function SommairePage({ onNavigate, journalData }) {
  const sections = [
    { id:'mode-emploi', label:"Guide d'utilisation", icon:'📖', color:'var(--or)' },
    { id:'profil', label:'Mon profil capillaire', icon:'👤', color:'var(--rose-moyen)' },
    { id:'engagements', label:'Mes engagements', icon:'🌿', color:'var(--rose-vieux)' },
    { id:'serment', label:'Le Serment de la Couronne', icon:'👑', color:'var(--or)' },
  ];

  const weeks = [
    { sem:'semaine-1', days:[1,2,3,4,5,6,7], label:'Semaine 1', quote:"Une semaine a la fois." },
    { sem:'semaine-2', days:[8,9,10,11,12,13,14], label:'Semaine 2', quote:"Plus je comprends mes cheveux..." },
    { sem:'semaine-3', days:[15,16,17,18,19,20,21], label:'Semaine 3', quote:"La régularité crée les résultats." },
    { sem:'semaine-4', days:[22,23,24,25,26,27,28], label:'Semaine 4', quote:"Fière du chemin parcouru." },
    { sem:'semaine-5', days:[29,30,31,32,33,34,35], label:'Semaine 5', quote:"Tu deviens régulière." },
    { sem:'semaine-6', days:[36,37,38,39,40,41,42], label:'Semaine 6', quote:"Ta couronne est en transformation." },
  ];

  const isDayDone = (dayNum) => {
    const d = journalData[`day-${dayNum}`];
    return d && (d.rituel?.length > 0 || d.humeur !== undefined);
  };

  return (
    <div className="page">
      <div style={{textAlign:'center', marginBottom:'20px'}}>
        <div style={{fontSize:'28px'}}>&#128081;</div>
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'22px', color:'var(--bordeaux)', marginTop:'8px'}}>
          Sommaire
        </div>
        <p style={{fontSize:'12px', color:'var(--taupe)', marginTop:'4px'}}>
          Clique sur une section pour y acceder directement
        </p>
      </div>

      {/* Sections fixes */}
      <div className="card" style={{marginBottom:'12px'}}>
        <div className="ctitle">Pages principales</div>
        {sections.map(s => (
          <div key={s.id}
            onClick={() => onNavigate(s.id)}
            style={{
              display:'flex', alignItems:'center', gap:'12px',
              padding:'10px 0', borderBottom:'1px solid var(--rose-poudre)',
              cursor:'pointer'
            }}>
            <div style={{
              width:'32px', height:'32px', borderRadius:'50%',
              background:'var(--rose-poudre)', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:'14px', flexShrink:0
            }}>{s.icon}</div>
            <div style={{flex:1, fontSize:'13px', fontWeight:'600', color:'var(--bordeaux)'}}>
              {s.label}
            </div>
            <div style={{fontSize:'16px', color:'var(--rose-pale)'}}>›</div>
          </div>
        ))}
      </div>

      {/* Semaines et jours */}
      {weeks.map(w => (
        <div key={w.sem} className="card" style={{marginBottom:'12px'}}>
          {/* Header semaine cliquable */}
          <div onClick={() => onNavigate(w.sem)}
            style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              marginBottom:'10px', cursor:'pointer',
              paddingBottom:'8px', borderBottom:'2px solid var(--or)'
            }}>
            <div>
              <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'16px', fontWeight:'600', color:'var(--bordeaux)'}}>
                {w.label}
              </div>
              <div style={{fontSize:'11px', fontStyle:'italic', color:'var(--rose-moyen)'}}>{w.quote}</div>
            </div>
            <div style={{
              background:'var(--bordeaux)', color:'white', fontSize:'11px',
              fontWeight:'700', padding:'4px 12px', borderRadius:'12px'
            }}>
              Voir
            </div>
          </div>

          {/* Grille des jours */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'6px'}}>
            {w.days.map(dayNum => {
              const done = isDayDone(dayNum);
              return (
                <div key={dayNum}
                  onClick={() => onNavigate(`day-${dayNum}`)}
                  style={{
                    aspectRatio:'1', borderRadius:'10px', display:'flex',
                    flexDirection:'column', alignItems:'center', justifyContent:'center',
                    cursor:'pointer', fontSize:'12px', fontWeight:'700',
                    background: done ? 'var(--rose-vieux)' : 'var(--creme-fonce)',
                    color: done ? 'white' : 'var(--taupe)',
                    border: done ? 'none' : '1px solid var(--rose-pale)',
                    position:'relative'
                  }}>
                  <span>{dayNum}</span>
                  {done && <span style={{fontSize:'8px', marginTop:'1px'}}>✓</span>}
                </div>
              );
            })}
          </div>

          {/* Progression semaine */}
      <div style={{marginTop:'8px', display:'flex', alignItems:'center', gap:'8px'}}>
            <div style={{flex:1, background:'var(--rose-poudre)', borderRadius:'20px', height:'4px'}}>
              <div style={{
                height:'4px', borderRadius:'20px',
                background:'linear-gradient(90deg, var(--rose-moyen), var(--or))',
                width:`${(w.days.filter(d => isDayDone(d)).length / 7) * 100}%`,
                transition:'width 0.5s'
              }}/>
            </div>
            <span style={{fontSize:'10px', color:'var(--taupe)', fontWeight:'700', minWidth:'30px'}}>
              {w.days.filter(d => isDayDone(d)).length}/7
            </span>
          </div>
        </div>
      ))}

      {/* Pages finales */}
      <div className="card">
        <div className="ctitle">Pour finir</div>
        {[
          { id:'produits', label:'Mes produits du mois', icon:'💧' },
          { id:'bilan', label:'Mon bilan 6 semaines', icon:'⭐' },
          { id:'faq', label:'FAQ & Aide', icon:'❓' },
        ].map(s => (
          <div key={s.id}
            onClick={() => onNavigate(s.id)}
            style={{
              display:'flex', alignItems:'center', gap:'12px',
              padding:'10px 0', borderBottom:'1px solid var(--rose-poudre)',
              cursor:'pointer'
            }}>
            <div style={{
              width:'32px', height:'32px', borderRadius:'50%',
              background:'var(--rose-poudre)', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:'14px'
            }}>{s.icon}</div>
            <div style={{flex:1, fontSize:'13px', fontWeight:'600', color:'var(--bordeaux)'}}>
              {s.label}
            </div>
            <div style={{fontSize:'16px', color:'var(--rose-pale)'}}>›</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BILAN PAGE ───────────────────────────────────────────────────────────────
function BilanPage({ data, onChange, userName, journalData }) {
  const update = (key, val) => onChange({ ...data, [key]: val });

  // ── Calcul automatique des stats ──
  const jours = journalData || {};
  const joursCompletes = Array.from({length:42}, (_,i) => {
    const d = jours[`day-${i+1}`];
    return d && (d.rituel?.length > 0 || d.humeur !== undefined);
  }).filter(Boolean).length;

  const pct = Math.round((joursCompletes / 42) * 100);

  // Humeur moyenne
  const moods = Array.from({length:42}, (_,i) => jours[`day-${i+1}`]?.humeur)
    .filter(m => m !== undefined && m !== null);
  const MOODS_LABELS = ['Difficile','Moyen','Neutre','Bien','Épanouie'];
  const avgMoodIdx = moods.length > 0
    ? Math.round(moods.reduce((a,b) => a+b, 0) / moods.length)
    : null;

  // Geste le plus pratiqué
  const GESTES_DEF = ['Mon geste 1','Mon geste 2','Mon geste 3','Hydratation (eau)','Protection','Geste positif pour moi'];
  const compteGestes = {};
  Array.from({length:42}, (_,i) => jours[`day-${i+1}`]?.rituel || [])
    .flat().forEach(g => { compteGestes[g] = (compteGestes[g] || 0) + 1; });
  const gesteTop = Object.entries(compteGestes).sort((a,b) => b[1]-a[1])[0];

  // Semaine la plus régulière
  const semainesStats = Array.from({length:6}, (_,s) => {
    const faits = Array.from({length:7}, (_,j) => {
      const d = jours[`day-${s*7+j+1}`];
      return d && (d.rituel?.length > 0 || d.humeur !== undefined);
    }).filter(Boolean).length;
    return faits;
  });
  const semTopIdx = semainesStats.indexOf(Math.max(...semainesStats));

  return (
    <div className="page">
      <div style={{textAlign:'center', marginBottom:'20px'}}>
        <img
          src={process.env.PUBLIC_URL + '/logo_full_medium.png'}
          alt="Missawoa"
          style={{ height: '100px', width: 'auto', marginBottom: '8px', filter: 'drop-shadow(0 4px 16px rgba(155,127,166,0.25))' }}
        />
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'26px', color:'var(--bordeaux)', marginTop:'4px'}}>
          Mon bilan · 6 semaines<br/>Ce qui a changé
        </div>
        <div style={{fontFamily:'Dancing Script, cursive', fontSize:'16px', color:'var(--rose-moyen)', marginTop:'8px'}}>
          Célèbre-toi, tu es en train de devenir ta meilleure version.
        </div>
      </div>

      {/* ── Stats automatiques ── */}
      <div className="card" style={{marginBottom:'16px'}}>
        <div className="card-title">📊 Mes chiffres du parcours</div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px'}}>
          {[
            { val: joursCompletes, label: 'Jours complétés', sub: 'sur 42', icon: '📅' },
            { val: `${pct}%`, label: 'Taux de régularité', sub: 'sur 6 semaines', icon: '🎯' },
            { val: avgMoodIdx !== null ? MOODS_LABELS[avgMoodIdx] : '—', label: 'Humeur moyenne', sub: 'sur le parcours', icon: '💫' },
            { val: semTopIdx >= 0 ? `Sem. ${semTopIdx+1}` : '—', label: 'Semaine la + régulière', sub: `${semainesStats[semTopIdx] || 0}/7 jours`, icon: '🏆' },
          ].map((s,i) => (
            <div key={i} style={{
              background:'var(--rose-poudre)', borderRadius:'12px',
              padding:'12px', textAlign:'center',
              border:'1px solid var(--rose-pale)'
            }}>
              <div style={{fontSize:'18px', marginBottom:'4px'}}>{s.icon}</div>
              <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'20px', fontWeight:'600', color:'var(--bordeaux)', lineHeight:1}}>
                {s.val}
              </div>
              <div style={{fontSize:'10px', fontWeight:'700', color:'var(--rose-vieux)', marginTop:'3px'}}>{s.label}</div>
              <div style={{fontSize:'9px', color:'var(--taupe)', marginTop:'1px'}}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Barre progression */}
        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
          <div style={{flex:1, background:'var(--rose-poudre)', borderRadius:'20px', height:'8px', overflow:'hidden'}}>
            <div style={{
              height:'100%', width:`${pct}%`,
              background:'linear-gradient(90deg, var(--violet), var(--rose-vieux))',
              borderRadius:'20px', transition:'width 1s ease'
            }}/>
          </div>
          <span style={{fontSize:'12px', fontWeight:'700', color:'var(--bordeaux)', minWidth:'32px'}}>{pct}%</span>
        </div>

        {gesteTop && (
          <div style={{
            marginTop:'12px', padding:'10px 12px',
            background:'var(--violet-light)', borderRadius:'10px',
            fontSize:'12px', color:'var(--bordeaux)'
          }}>
            <span style={{fontWeight:'700'}}>Ton geste fétiche 🌿 </span>
            {gesteTop[0]} — pratiqué {gesteTop[1]} fois
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">💭 Mon ressenti</div>
        <Field label="Comment je me sens aujourd'hui ?" value={data.ressenti}
          onChange={v => update('ressenti', v)} multiline rows={3} />
        <Field label="Ma plus grande fierté ?" value={data.fierte}
          onChange={v => update('fierte', v)} multiline rows={2} />
        <Field label="Qu'est-ce qui a changé chez moi ?" value={data.changement}
          onChange={v => update('changement', v)} multiline rows={2} />
      </div>

      <div className="card">
        <div className="card-title">✅ Mes progrès</div>
        <div className="check-grid">
          {['Mes cheveux sont plus hydratés', 'Mes cheveux sont plus forts',
            'Ma chevelure est plus douce', 'Ma définition des boucles s\'est améliorée',
            'Je comprends mieux mes cheveux', 'Je suis plus régulière et constante',
            'J\'ai trouvé une routine adaptée à moi'].map(p => (
            <CheckItem key={p} label={p}
              checked={(data.progres || []).includes(p)}
              onChange={checked => {
                const curr = data.progres || [];
                update('progres', checked ? [...curr, p] : curr.filter(x => x !== p));
              }} />
          ))}
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>📖 Ce que j'ai appris</div>
          <Field label="Mes cheveux ont besoin de..." value={data.besoin}
            onChange={v => update('besoin', v)} multiline rows={2} />
          <Field label="Ce qui fonctionne pour moi..." value={data.fonctionne}
            onChange={v => update('fonctionne', v)} multiline rows={2} />
          <Field label="Ce que j'évite désormais..." value={data.evite}
            onChange={v => update('evite', v)} multiline rows={2} />
        </div>
        <div className="card">
          <div className="card-title" style={{fontSize:'13px'}}>⭐ Ma note générale</div>
          <p style={{fontSize:'12px', color:'var(--taupe)', marginBottom:'10px'}}>Sur 5, comment je note ma progression ?</p>
          <Stars value={data.note || 0} onChange={v => update('note', v)} />
          <Field label="Pourquoi cette note ?" value={data.noteExplication}
            onChange={v => update('noteExplication', v)} multiline rows={3} />
        </div>
      </div>

      <div className="card">
        <div className="card-title">🌿 Mon engagement pour la suite</div>
        <Field value={data.engagement} onChange={v => update('engagement', v)}
          multiline rows={3} placeholder="À partir d'aujourd'hui, je décide de..." />
        <div style={{textAlign:'center', padding:'12px', background:'var(--rose-poudre)', borderRadius:'10px', marginTop:'8px', fontFamily:'Dancing Script, cursive', fontSize:'15px', color:'var(--bordeaux)'}}>
          "La régularité d'aujourd'hui crée la chevelure de demain."
        </div>
      </div>

      <div className="card">
        <div className="card-title">✍️ Ma signature</div>
        <p style={{fontSize:'13px', color:'var(--taupe)', marginBottom:'12px'}}>Je m'engage envers moi-même.</p>
        <Field label="Nom / Surnom" value={data.nomBilan} onChange={v => update('nomBilan', v)} />
        <Field label="Signature" value={data.signatureBilan} onChange={v => update('signatureBilan', v)} placeholder="Ta signature..." />
        <Field label="Date" value={data.dateBilan} onChange={v => update('dateBilan', v)} placeholder="__ / __ / ____" />
        <div style={{textAlign:'center', marginTop:'16px', padding:'12px', background:'var(--bordeaux)', borderRadius:'10px', color:'var(--blanc)', fontSize:'12px', lineHeight:'1.6'}}>
          Je ne suis plus la même.<br/>Je suis plus consciente, plus constante, plus forte.<br/>
          <span style={{fontFamily:'Dancing Script, cursive', fontSize:'14px', color:'var(--or-clair)'}}>Ma couronne est en construction, et elle brille déjà.</span>
        </div>
        {/* Certificat PDF */}
        <div style={{textAlign:'center', marginTop:'16px'}}>
          <button
            onClick={() => {
              if (!window.jspdf) {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                script.onload = () => generateCertificat(userName, data);
                document.head.appendChild(script);
              } else {
                generateCertificat(userName, data);
              }
            }}
            style={{
              padding:'14px 28px', borderRadius:'50px', border:'none',
              background:'linear-gradient(135deg, var(--or), var(--or-clair))',
              color:'var(--bordeaux)', fontFamily:'Nunito, sans-serif',
              fontSize:'14px', fontWeight:'700', cursor:'pointer',
              boxShadow:'0 4px 16px rgba(201,160,80,0.4)'
            }}>
            Telecharger mon Certificat de Reussite
          </button>
          <div style={{fontSize:'11px', color:'var(--taupe)', marginTop:'6px'}}>
            Un souvenir de ton parcours capillaire
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CERTIFICAT PDF ──────────────────────────────────────────────────────────
function generateCertificat(userName, stats) {
  const { jsPDF } = window.jspdf || {};
  if (!jsPDF) {
    alert("Chargement en cours, reessaie dans quelques secondes.");
    return;
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297;

  // Fond
  doc.setFillColor(253, 248, 244);
  doc.rect(0, 0, W, H, 'F');

  // Bordure decorative
  doc.setDrawColor(201, 160, 80);
  doc.setLineWidth(2);
  doc.rect(10, 10, W-20, H-20);
  doc.setLineWidth(0.5);
  doc.rect(14, 14, W-28, H-28);

  // Titre
  doc.setTextColor(107, 30, 46);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('MISSAWOA · COACHING CAPILLAIRE · GABON', W/2, 35, { align:'center' });

  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(107, 30, 46);
  doc.text('CERTIFICAT', W/2, 60, { align:'center' });

  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(194, 113, 122);
  doc.text('de Reussite Capillaire', W/2, 72, { align:'center' });

  // Ligne or
  doc.setDrawColor(201, 160, 80);
  doc.setLineWidth(1);
  doc.line(40, 80, W-40, 80);

  // Corps
  doc.setFontSize(13);
  doc.setTextColor(46, 26, 28);
  doc.setFont('helvetica', 'normal');
  doc.text('Ce certificat est decerne a', W/2, 100, { align:'center' });

  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(107, 30, 46);
  doc.text(userName, W/2, 116, { align:'center' });

  doc.setFontSize(13);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(46, 26, 28);
  doc.text('pour avoir complete son parcours capillaire de', W/2, 132, { align:'center' });

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(201, 160, 80);
  doc.text('42 jours · 6 semaines', W/2, 146, { align:'center' });

  // Stats
  doc.setLineWidth(0.5);
  doc.setDrawColor(232, 180, 184);
  doc.setFillColor(245, 222, 224);
  doc.roundedRect(30, 158, W-60, 50, 4, 4, 'FD');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(107, 30, 46);
  doc.text(`${stats.daysCompleted || 0} jours complétés`, W/2 - 35, 172, { align:'center' });
  doc.text(`${stats.regularity || '-'} régularité`, W/2 + 35, 172, { align:'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(158, 126, 130);
  doc.text(`sur 42 jours`, W/2 - 35, 180, { align:'center' });
  doc.text(`sur les 7 derniers jours`, W/2 + 35, 180, { align:'center' });

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(107, 30, 46);
  doc.text("Comprendre tes cheveux, c'est reprendre le pouvoir.", W/2, 198, { align:'center' });

  // Date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(158, 126, 130);
  doc.text(`Delivre le ${new Date().toLocaleDateString('fr-FR')}`, W/2, 220, { align:'center' });

  // Signature
  doc.setDrawColor(201, 160, 80);
  doc.setLineWidth(0.8);
  doc.line(W/2 - 30, 250, W/2 + 30, 250);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(107, 30, 46);
  doc.text('Coach Missawoa', W/2, 258, { align:'center' });
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(158, 126, 130);
  doc.text('Coaching capillaire · Gabon', W/2, 265, { align:'center' });

  doc.save(`Certificat-Missawoa-${userName}.pdf`);
}

// ─── PRODUITS PAGE ────────────────────────────────────────────────────────────
function ProduitsPage({ data, onChange }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="page">
      <div className="card">
        <div className="card-title">👑 Les 4 indispensables Missawoa</div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
          {[
            {num:'1', name:'Shampooing doux', desc:'Nettoie sans agresser.'},
            {num:'2', name:'Masque', desc:'Nourrit et répare en profondeur.'},
            {num:'3', name:'Lait capillaire', desc:'Hydrate, assouplit et facilite le coiffage.'},
            {num:'4', name:'Huile / Karité', desc:'Scelle l\'hydratation et protège.'},
          ].map(p => (
            <div key={p.num} style={{background:'var(--creme)', borderRadius:'10px', padding:'12px', border:'1px solid var(--rose-poudre)'}}>
              <div style={{fontWeight:'700', fontSize:'12px', color:'var(--bordeaux)', marginBottom:'4px'}}>{p.num}. {p.name}</div>
              <div style={{fontSize:'11px', color:'var(--taupe)', lineHeight:'1.4'}}>{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-title">📝 Mes produits utilisés</div>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px', padding:'10px', background:'var(--creme)', borderRadius:'10px'}}>
            <div style={{gridColumn:'1/-1', fontWeight:'700', fontSize:'11px', color:'var(--bordeaux)'}}>Produit {i}</div>
            <Field value={(data.produits || {})[`${i}_nom`]}
              onChange={v => update('produits', {...(data.produits||{}), [`${i}_nom`]:v})}
              placeholder="Nom du produit..." />
            <Field value={(data.produits || {})[`${i}_marque`]}
              onChange={v => update('produits', {...(data.produits||{}), [`${i}_marque`]:v})}
              placeholder="Marque..." />
            <div style={{gridColumn:'1/-1'}}>
              <label style={{fontSize:'10px', fontWeight:'700', color:'var(--taupe)', display:'block', marginBottom:'4px'}}>Avis</label>
              <OptionPicker options={['J\'aime beaucoup', 'J\'aime moyennement', 'Je n\'aime pas']}
                value={(data.produits || {})[`${i}_avis`]}
                onChange={v => update('produits', {...(data.produits||{}), [`${i}_avis`]:v})} />
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-title">💛 Mes coups de cœur</div>
        {[1,2,3].map(i => (
          <Field key={i} label={`Coup de cœur ${i}`}
            value={(data.coups || {})[i]}
            onChange={v => update('coups', {...(data.coups||{}), [i]:v})}
            placeholder="Produit que j'adore..." />
        ))}
      </div>
    </div>
  );
}

// ─── BANNIÈRE INSTALLATION ───────────────────────────────────────────────────
function InstallBanner({ daysCompleted, onDismiss }) {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showManual, setShowManual] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem('mj-install-dismissed') === 'true'
  );

  React.useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // N'afficher qu'à partir du 3ème jour et si pas dismissed
  if (dismissed || daysCompleted < 3) return null;

  // Si déjà installé comme PWA
  if (window.matchMedia('(display-mode: standalone)').matches) return null;

  const dismiss = () => {
    localStorage.setItem('mj-install-dismissed', 'true');
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') dismiss();
    } else {
      setShowManual(true);
    }
  };

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--violet), var(--bordeaux))',
      padding: '14px 16px', margin: '0',
    }}>
      {!showManual ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px', flexShrink: 0 }}>📲</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: 'white', marginBottom: '2px' }}>
              Ajoute le journal à ton écran d'accueil
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
              Pour y accéder en un tap, comme une vraie app.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <button onClick={handleInstall}
              style={{
                background: 'white', color: 'var(--bordeaux)', border: 'none',
                borderRadius: '20px', padding: '7px 14px',
                fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
              }}>
              Ajouter
            </button>
            <button onClick={dismiss}
              style={{
                background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
                borderRadius: '20px', padding: '7px 10px',
                fontSize: '12px', cursor: 'pointer',
              }}>
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: 'white' }}>
              📲 Comment ajouter à l'écran d'accueil
            </div>
            <button onClick={dismiss}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '16px', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
          {isIOS ? (
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>
              1. Appuie sur <strong style={{color:'white'}}>⎙ Partager</strong> en bas de Safari<br/>
              2. Fais défiler et choisis <strong style={{color:'white'}}>"Sur l'écran d'accueil"</strong><br/>
              3. Appuie sur <strong style={{color:'white'}}>"Ajouter"</strong> en haut à droite
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>
              1. Appuie sur le menu <strong style={{color:'white'}}>⋮</strong> en haut à droite<br/>
              2. Choisis <strong style={{color:'white'}}>"Ajouter à l'écran d'accueil"</strong><br/>
              3. Confirme en appuyant sur <strong style={{color:'white'}}>"Ajouter"</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MESSAGE COACH ───────────────────────────────────────────────────────────
function CoachMessageBanner({ clientId }) {
  const [message, setMessage] = React.useState(null);

  React.useEffect(() => {
    async function loadMsg() {
      try {
        const { loadClientData } = await import('./firebase');
        const data = await loadClientData(`msg-${clientId}`);
        if (data?.message && data?.active) setMessage(data);
      } catch(e) {}
    }
    if (clientId) loadMsg();
  }, [clientId]);

  if (!message) return null;

  return (
    <div style={{
      background:'linear-gradient(135deg, var(--bordeaux), var(--rose-vieux))',
      margin:'0 0 0 0', padding:'16px 20px',
      display:'flex', gap:'12px', alignItems:'flex-start'
    }}>
      <div style={{fontSize:'24px', flexShrink:0}}>&#128081;</div>
      <div>
        <div style={{fontSize:'10px', fontWeight:'700', color:'var(--rose-pale)', letterSpacing:'2px', marginBottom:'4px'}}>
          MESSAGE DE TA COACH MISSAWOA
        </div>
        <div style={{fontSize:'13px', color:'white', lineHeight:'1.6', fontStyle:'italic'}}>
          {message.message}
        </div>
        {message.date && (
          <div style={{fontSize:'10px', color:'rgba(255,255,255,0.5)', marginTop:'6px'}}>
            {new Date(message.date).toLocaleDateString('fr-FR')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MODE EMPLOI ─────────────────────────────────────────────────────────────
function ModeEmploiPage() {
  const steps = [
    { icon: "1", titre: "Ton profil", desc: "Commence par remplir ton profil capillaire. Type, porosite, densite. C'est la base de tout." },
    { icon: "2", titre: "Tes engagements", desc: "Note tes 3 gestes issus du diagnostic Missawoa. Ce sont TES gestes, personnalises pour toi." },
    { icon: "3", titre: "Le Serment", desc: "Signe le Serment de la Couronne. C'est ton engagement envers toi-meme. Prends-le au serieux." },
    { icon: "4", titre: "Chaque semaine", desc: "En début de semaine, remplis la page Semaine. Ton intention, ton objectif, tes produits." },
    { icon: "5", titre: "Chaque jour", desc: "Prends 2 a 5 minutes pour ta page Jour. Tes gestes, l'etat de tes cheveux, ton humeur." },
    { icon: "6", titre: "Le bilan", desc: "À la fin des 6 semaines, remplis ton bilan. C'est là que tu mesures ton vrai chemin." },
  ];

  return (
    <div className="page">
      <div style={{textAlign:'center', marginBottom:'20px'}}>
        <img
          src={process.env.PUBLIC_URL + '/logo_tiny.png'}
          alt="Missawoa"
          style={{ height: '56px', width: 'auto', filter: 'drop-shadow(0 3px 10px rgba(155,127,166,0.2))' }}
        />
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'22px', color:'var(--bordeaux)', marginTop:'8px'}}>
          Comment utiliser ce journal
        </div>
        <p style={{fontSize:'13px', color:'var(--taupe)', marginTop:'6px', lineHeight:'1.5'}}>
          L'objectif n'est pas la perfection, mais la régularité.
        </p>
      </div>

      {steps.map((s, i) => (
        <div key={i} className="card" style={{display:'flex', gap:'14px', alignItems:'flex-start'}}>
          <div style={{
            width:'40px', height:'40px', borderRadius:'50%', flexShrink:0,
            background:'var(--rose-poudre)', display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:'18px'
          }}>{s.icon}</div>
          <div>
            <div style={{fontWeight:'700', color:'var(--bordeaux)', fontSize:'14px', marginBottom:'4px'}}>{s.titre}</div>
            <div style={{fontSize:'13px', color:'var(--brun)', lineHeight:'1.6'}}>{s.desc}</div>
          </div>
        </div>
      ))}

      <div className="card" style={{background:'var(--bordeaux)', border:'none'}}>
        <div style={{color:'white', fontSize:'13px', lineHeight:'1.7', textAlign:'center'}}>
          <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'18px', marginBottom:'8px'}}>
            Ta seule mission
          </div>
          <div style={{fontStyle:'italic', color:'var(--rose-pale)'}}>
            Observer · Comprendre · Être régulière
          </div>
          <div style={{marginTop:'8px', fontSize:'12px', color:'rgba(255,255,255,0.7)'}}>
            Le reste viendra.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQPage() {
  const [open, setOpen] = useState(null);

  const faqs = [
    { q: "Je dois remplir chaque jour sans exception ?", r: "Non. Si tu oublies un jour, tu continues le lendemain. Ce qui compte c'est d'avancer, pas d'être parfaite." },
    { q: "Mes donnees sont-elles privees ?", r: "Oui. Tes données sont sauvegardées de façon sécurisée. Seule ton coach peut voir tes statistiques générales." },
    { q: "Je peux utiliser ce journal sur plusieurs appareils ?", r: "Oui. Ton lien est unique. Tu peux l'ouvrir depuis ton telephone, ta tablette ou ton ordinateur." },
    { q: "Que faire si je n'arrive pas a remplir une section ?", r: "Laisse-la vide et passe à la suivante. Le journal s'adapte à toi, pas l'inverse." },
    { q: "Les gestes correspondent a quoi ?", r: "Ce sont les 3 gestes personnalises definis lors de ton diagnostic Missawoa." },
    { q: "Que se passe-t-il apres les 42 jours ?", r: "Tu remplis ton bilan final et tu contactes ta coach Missawoa pour un nouveau diagnostic et la suite de ton accompagnement." },
    { q: "Qui peut voir mes notes personnelles ?", r: "Tes donnees sont stockees de facon securisee. Ton coach peut voir tes statistiques générales (jours complétés, régularité, humeur moyenne) mais pas le contenu de tes notes personnelles." },
    { q: "J'ai un probleme technique, que faire ?", r: "Contacte ta coach Missawoa directement sur WhatsApp au +241 74 08 30 86 ou sur Instagram @missawoa." },
  ];

  return (
    <div className="page">
      <div style={{textAlign:'center', marginBottom:'20px'}}>
        <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'22px', color:'var(--bordeaux)'}}>
          Questions fréquentes
        </div>
        <p style={{fontSize:'13px', color:'var(--taupe)', marginTop:'6px'}}>
          Tout ce que tu dois savoir sur ton journal.
        </p>
      </div>

      {faqs.map((faq, i) => (
        <div key={i} className="card" style={{cursor:'pointer', marginBottom:'8px'}}
          onClick={() => setOpen(open === i ? null : i)}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px'}}>
            <div style={{fontWeight:'700', fontSize:'13px', color:'var(--bordeaux)', lineHeight:'1.4'}}>
              {faq.q}
            </div>
            <div style={{
              fontSize:'18px', color:'var(--rose-moyen)', flexShrink:0,
              transform: open === i ? 'rotate(45deg)' : 'none', transition:'transform 0.2s'
            }}>+</div>
          </div>
          {open === i && (
            <div style={{
              marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--rose-poudre)',
              fontSize:'13px', color:'var(--brun)', lineHeight:'1.7'
            }}>
              {faq.r}
            </div>
          )}
        </div>
      ))}

      <div className="card" style={{
        background:'linear-gradient(135deg, var(--bordeaux), var(--rose-vieux))',
        border:'none', textAlign:'center', marginTop:'8px'
      }}>
        <div style={{color:'white', fontSize:'13px', lineHeight:'1.7'}}>
          <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'18px', marginBottom:'8px'}}>
            Une question sans réponse ici ?
          </div>
          <div style={{color:'var(--rose-pale)', marginBottom:'16px'}}>
            Ta coach Missawoa est la pour toi.
          </div>
          <a href="https://wa.me/24174083086?text=Bonjour%20Missawoa%2C%20j'ai%20une%20question%20sur%20mon%20journal%20capillaire%20:"
            target="_blank" rel="noreferrer"
            style={{
              display:'inline-flex', alignItems:'center', gap:'8px',
              background:'white', color:'var(--bordeaux)',
              padding:'12px 24px', borderRadius:'24px', fontWeight:'700',
              fontSize:'13px', textDecoration:'none', marginBottom:'8px'
            }}>
            Envoyer une question sur WhatsApp
          </a>
          <div style={{fontSize:'12px', color:'rgba(255,255,255,0.7)', marginTop:'8px'}}>
            Instagram : @missawoa
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COACH VIEW ───────────────────────────────────────────────────────────────
function CoachView({ onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (authenticated) loadClients();
  }, [authenticated]);

  async function loadClients() {
    setLoading(true);
    try {
      const list = await loadAllClients();
      // Enrichir avec les stats du journal de chaque cliente
      const enriched = await Promise.all(list.map(async (c) => {
        const journal = await loadClientData(c.id);
        return {
          ...c,
          stats: journal?.stats || {},
          lastActivity: journal?.lastActivity || null,
        };
      }));
      // Trier : inactives en premier (à relancer), puis par dernière activité
      enriched.sort((a,b) => {
        const da = a.lastActivity ? new Date(a.lastActivity) : new Date(0);
        const db = b.lastActivity ? new Date(b.lastActivity) : new Date(0);
        return db - da;
      });
      setClients(enriched);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function addClient() {
    if (!newClient.trim()) return;
    const id = newClient.trim().toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
    const client = {
      id,
      name: newClient.trim(),
      createdAt: new Date().toISOString(),
      link: `${window.location.origin}?client=${id}`,
    };
    await createClient(client);
    setClients(prev => [client, ...prev]);
    setNewClient('');
    setToast(`Lien créé pour ${newClient.trim()} !`);
    setTimeout(() => setToast(''), 3000);
  }

  async function removeClient(clientId, name) {
    if (!window.confirm(`Supprimer la cliente ${name} ? Cette action est irréversible.`)) return;
    await deleteClient(clientId);
    setClients(prev => prev.filter(c => c.id !== clientId));
    setToast(`${name} supprimée.`);
    setTimeout(() => setToast(''), 2500);
  }

  function copyLink(link) {
    navigator.clipboard.writeText(link);
    setToast('Lien copié !');
    setTimeout(() => setToast(''), 2000);
  }

  async function handleLogin() {
    if (!email.trim() || !password.trim()) {
      setLoginError('Remplis ton email et ton mot de passe.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    const result = await loginCoach(email.trim(), password);
    setLoginLoading(false);
    if (result.success) {
      setAuthenticated(true);
    } else {
      setLoginError(result.error);
    }
  }

  async function handleLogout() {
    await logoutCoach();
    setAuthenticated(false);
    setEmail('');
    setPassword('');
  }

  if (!authenticated) {
    return (
      <div className="coach-login">
        <div style={{textAlign:'center', marginBottom:'32px'}}>
          <img
            src={process.env.PUBLIC_URL + '/logo_cover.png'}
            alt="Missawoa"
            style={{ height: '80px', width: 'auto', marginBottom: '4px', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.3))' }}
          />
          <div style={{fontFamily:'Cormorant Garamond, serif', fontSize:'28px', color:'white', marginTop:'4px'}}>
            Espace Coach
          </div>
          <div style={{color:'var(--rose-pale)', fontSize:'13px', marginTop:'4px'}}>Missawoa</div>
        </div>
        <div style={{width:'100%', maxWidth:'320px', background:'white', borderRadius:'20px', padding:'24px'}}>
          <div className="field">
            <label className="label" style={{fontSize:'11px', fontWeight:'700', color:'var(--rose-vieux)', letterSpacing:'1px', textTransform:'uppercase', display:'block', marginBottom:'6px'}}>
              Email
            </label>
            <input className="inp" type="email" placeholder="ton@email.com"
              value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          </div>
          <div className="field">
            <label className="label" style={{fontSize:'11px', fontWeight:'700', color:'var(--rose-vieux)', letterSpacing:'1px', textTransform:'uppercase', display:'block', marginBottom:'6px'}}>
              Mot de passe
            </label>
            <input className="inp" type="password" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          </div>
          {loginError && (
            <div style={{color:'var(--rose-vieux)', fontSize:'12px', marginBottom:'12px',
              padding:'8px 12px', background:'var(--rose-poudre)', borderRadius:'8px'}}>
              {loginError}
            </div>
          )}
          <button className="btn btn-primary btn-block"
            style={{marginTop:'8px', opacity: loginLoading ? 0.7 : 1}}
            onClick={handleLogin}
            disabled={loginLoading}>
            {loginLoading ? 'Connexion...' : 'Acceder'}
          </button>
          <button className="btn btn-secondary btn-block" style={{marginTop:'8px'}} onClick={onBack}>
            Retour
          </button>
        </div>
        <Toast msg={toast} />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <div className="header-brand">
          <span className="header-crown">👑</span>
          <div>
            <div className="header-title">Espace Coach</div>
            <div className="header-sub">Missawoa</div>
          </div>
        </div>
        <div style={{display:'flex', gap:'8px'}}>
          <button className="btn btn-secondary btn-sm" onClick={handleLogout}>
            Deconnexion
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onBack}>Retour</button>
        </div>
      </div>

      <div className="page">
        <div className="card">
          <div className="card-title">➕ Nouvelle cliente</div>
          <div style={{display:'flex', gap:'10px'}}>
            <div style={{flex:1}}>
              <Field value={newClient} onChange={setNewClient} placeholder="Prénom de la cliente..." />
            </div>
            <button className="btn btn-primary" style={{alignSelf:'flex-end', marginBottom:'14px'}}
              onClick={addClient}>Créer</button>
          </div>
        </div>

        {loading && <div style={{textAlign:'center', color:'var(--taupe)', padding:'20px'}}>Chargement...</div>}

        <div className="coach-card-list">
          {clients.length === 0 && !loading && (
            <div style={{textAlign:'center', color:'var(--taupe)', padding:'20px', fontSize:'14px'}}>
              Aucune cliente pour le moment.<br/>Crée un premier lien ci-dessus.
            </div>
          )}

          {/* Résumé stats */}
          {clients.length > 0 && (
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px'}}>
              {[
                { label:'Clientes', val: clients.length },
                { label:'Actives', val: clients.filter(c => {
                  if (!c.lastActivity) return false;
                  const days = (Date.now() - new Date(c.lastActivity)) / (1000*60*60*24);
                  return days <= 3;
                }).length },
                { label:'Inactives +3j', val: clients.filter(c => {
                  if (!c.lastActivity) return true;
                  const days = (Date.now() - new Date(c.lastActivity)) / (1000*60*60*24);
                  return days > 3;
                }).length },
              ].map((s,i) => (
                <div key={i} style={{
                  background:'var(--creme-fonce)', borderRadius:'12px', padding:'12px',
                  textAlign:'center', border:'1px solid var(--rose-poudre)'
                }}>
                  <div style={{fontSize:'22px', fontWeight:'700', color:'var(--bordeaux)', fontFamily:'Cormorant Garamond, serif'}}>{s.val}</div>
                  <div style={{fontSize:'10px', color:'var(--taupe)', fontWeight:'700', letterSpacing:'0.5px'}}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="coach-card-list">
          {clients.map(c => {
            const stats = c.stats || {};
            const lastActivity = c.lastActivity ? new Date(c.lastActivity) : null;
            const daysSince = lastActivity
              ? Math.floor((Date.now() - lastActivity) / (1000*60*60*24))
              : null;
            const isInactive = daysSince === null || daysSince > 3;
            const moodEmojis = ['😣','😕','😐','🙂','😊'];

            return (
              <div key={c.id} className="client-card" style={{
                borderLeft: isInactive ? '3px solid var(--rose-moyen)' : '3px solid #4CAF50',
                flexDirection: 'column', gap:'10px'
              }}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                  <div className="client-avatar">{c.name[0].toUpperCase()}</div>
                  <div className="client-info" style={{flex:1}}>
                    <div className="client-name">{c.name}</div>
                    <div className="client-stats">
                      {lastActivity
                        ? daysSince === 0 ? "Active aujourd'hui"
                          : daysSince === 1 ? "Active hier"
                          : `Inactive depuis ${daysSince} jours`
                        : "Jamais connectee"}
                    </div>
                  </div>
                  <div style={{
                    fontSize:'10px', fontWeight:'700', padding:'4px 10px',
                    borderRadius:'12px',
                    background: isInactive ? 'var(--rose-poudre)' : 'rgba(76,175,80,0.1)',
                    color: isInactive ? 'var(--rose-moyen)' : '#2E7D32'
                  }}>
                    {isInactive ? 'À relancer' : 'Active'}
                  </div>
                </div>

                {/* Message coach */}
                <div style={{marginBottom:'8px'}}>
                  <input
                    placeholder="Ecrire un message pour cette cliente..."
                    style={{
                      width:'100%', border:'1px solid var(--rose-pale)', borderRadius:'8px',
                      padding:'8px 12px', fontSize:'12px', fontFamily:'Nunito, sans-serif',
                      background:'var(--creme)', color:'var(--brun)', outline:'none'
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const { saveClientData } = await import('./firebase');
                        await saveClientData(`msg-${c.id}`, {
                          message: e.target.value.trim(),
                          date: new Date().toISOString(),
                          active: true
                        });
                        e.target.value = '';
                        setToast('Message envoye !');
                        setTimeout(() => setToast(''), 2500);
                      }
                    }}
                  />
                  <div style={{fontSize:'10px', color:'var(--taupe)', marginTop:'3px'}}>
                    Appuie sur Entree pour envoyer
                  </div>
                </div>

          {/* Stats */}
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px'}}>
                  {[
                    { label:'Jours', val: `${stats.daysCompleted || 0}/42` },
                    { label:'Régularité', val: stats.regularity || '-' },
                    { label:'Humeur moy.', val: stats.avgMood !== null && stats.avgMood !== undefined ? moodEmojis[stats.avgMood] : '-' },
                  ].map((s,i) => (
                    <div key={i} style={{
                      background:'var(--creme)', borderRadius:'8px', padding:'8px',
                      textAlign:'center', border:'1px solid var(--rose-poudre)'
                    }}>
                      <div style={{fontSize:'14px', fontWeight:'700', color:'var(--bordeaux)'}}>{s.val}</div>
                      <div style={{fontSize:'9px', color:'var(--taupe)', fontWeight:'700', letterSpacing:'0.5px'}}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{display:'flex', gap:'8px'}}>
                  <button className="btn btn-primary btn-sm" style={{flex:1}}
                    onClick={() => copyLink(c.link)}>
                    Copier lien
                  </button>
                  {isInactive && (
                    <button className="btn btn-secondary btn-sm" style={{flex:1}}
                      onClick={() => {
                        const msg = `Bonjour ${c.name} 🌿 Je voulais prendre de tes nouvelles. Comment avancent tes cheveux ? N'oublie pas ton journal de bord !`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                      }}>
                      Relancer
                    </button>
                  )}
                  <button style={{
                    padding:'8px 12px', borderRadius:'20px', border:'1px solid var(--rose-pale)',
                    background:'none', color:'var(--rose-moyen)', fontSize:'11px',
                    cursor:'pointer', fontWeight:'700'
                  }} onClick={() => removeClient(c.id, c.name)}>
                    Supprimer
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
      <Toast msg={toast} />
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState('cover'); // cover | welcome | journal | coach
  const [userName, setUserName] = useState('');
  const [activeTab, setActiveTab] = useState('profil');
  const [journalData, setJournalData] = useState({});
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState(null);
  const isOnline = useOnlineStatus();

  // Check URL params pour lien client
  // Si pas de paramètre URL → chercher dans localStorage (cas app épinglée)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cidFromUrl = params.get('client');
    if (cidFromUrl) {
      localStorage.setItem('mj-client-id', cidFromUrl);
      setClientId(cidFromUrl);
    } else {
      const cidFromStorage = localStorage.getItem('mj-client-id');
      if (cidFromStorage) setClientId(cidFromStorage);
    }
  }, []);

  // Chargement initial depuis Firebase
  useEffect(() => {
    async function init() {
      const cid = clientId || 'anonymous';
      const saved = await loadClientData(cid);
      if (saved) {
        setJournalData(saved.data || {});
        setUserName(saved.userName || '');
        if (saved.userName) setView('welcome');
      }
    }
    init();
  }, [clientId]);

  // Calcul stats automatiques
  const computeStats = useCallback((data) => {
    const TOTAL_DAYS = 42;
    const daysCompleted = [...Array(TOTAL_DAYS)].filter((_,i) => {
      const d = data[`day-${i+1}`];
      return d && (d.rituel?.length > 0 || d.humeur !== undefined);
    }).length;

    const moods = [...Array(TOTAL_DAYS)].map((_,i) => data[`day-${i+1}`]?.humeur)
      .filter(m => m !== undefined);
    const avgMood = moods.length > 0
      ? Math.round(moods.reduce((a,b) => a+b, 0) / moods.length)
      : null;

    // Regularite : jours remplis sur les 7 derniers jours
    const last7 = [...Array(7)].filter((_,i) => {
      const d = data[`day-${TOTAL_DAYS-i}`];
      return d && (d.rituel?.length > 0 || d.humeur !== undefined);
    }).length;
    const regularity = last7 >= 5 ? 'Excellente' : last7 >= 3 ? 'Bonne' : last7 >= 1 ? 'Faible' : 'Inactive';

    return { daysCompleted, avgMood, regularity, last7, total: TOTAL_DAYS };
  }, []);

  // Autosave avec fallback localStorage si offline
  const saveJournal = useCallback(async (data, name) => {
    setSaving(true);
    const cid = clientId || 'anonymous';
    const stats = computeStats(data);
    const payload = {
      userName: name || userName,
      data,
      stats,
      lastActivity: new Date().toISOString(),
      savedAt: new Date().toISOString()
    };
    // Toujours sauvegarder en local (backup)
    try { localStorage.setItem(`mj-backup-${cid}`, JSON.stringify(payload)); } catch(e) {}
    // Sauvegarder sur Firebase si online
    if (navigator.onLine) {
      await saveClientData(cid, payload);
    }
    setSaving(false);
  }, [userName, clientId, computeStats]);

  // Confirmation avant de quitter si donnees non sauvegardees
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saving]);

  // Sync local -> Firebase quand on revient online
  React.useEffect(() => {
    if (isOnline && clientId) {
      const backup = localStorage.getItem(`mj-backup-${clientId}`);
      if (backup) {
        try {
          const data = JSON.parse(backup);
          saveClientData(clientId, data);
        } catch(e) {}
      }
    }
  }, [isOnline, clientId]);

  const updateSection = useCallback((section, val) => {
    setJournalData(prev => {
      const next = { ...prev, [section]: val };
      saveJournal(next, userName);
      return next;
    });
  }, [userName, saveJournal]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleStart = async (name) => {
    setUserName(name);
    setView('welcome'); // passe par la page d'accueil
    const cid = clientId || 'anonymous';
    if (!journalData.userName) {
      await saveClientData(cid, { userName: name, data: {}, savedAt: new Date().toISOString() });
    }
  };

  // Calcul du jour actuel
  const currentDay = Object.keys(journalData).filter(k => k.startsWith('day-')).length || 1;

  const daysCompleted = [...Array(42)].filter((_,i) => {
    const d = journalData[`day-${i+1}`];
    return d && (d.rituel?.length > 0 || d.humeur !== undefined);
  }).length;

  // Navigation tabs
  // Déterminer la section active pour la bottom nav
  const getNavSection = () => {
    if (activeTab === 'sommaire') return 'journal';
    if (activeTab.startsWith('day-') || activeTab.startsWith('semaine-')) return 'journal';
    if (['profil','engagements','serment','produits','bilan'].includes(activeTab)) return 'moi';
    if (['mode-emploi','faq'].includes(activeTab)) return 'plus';
    return 'accueil';
  };
  const navSection = getNavSection();

  // Trouver le prochain jour pour le bouton Journal
  let prochainJourNav = 1;
  for (let i = 1; i <= 42; i++) {
    const d = journalData[`day-${i}`];
    if (!d || (d.rituel?.length === 0 && d.humeur === undefined)) { prochainJourNav = i; break; }
    if (i === 42) prochainJourNav = 42;
  }

  if (view === 'coach') return <CoachView onBack={() => setView('cover')} />;

  if (view === 'welcome') {
    return (
      <>
        <style>{CSS}</style>
        <WelcomePage
          userName={userName}
          daysCompleted={daysCompleted}
          journalData={journalData}
          coachMessage={journalData._coachMessage || null}
          onEnter={(tabId) => { setView('journal'); setActiveTab(tabId || 'sommaire'); }}
          onGoToDay={(dayNum) => { setView('journal'); setActiveTab('day-' + dayNum); }}
        />
      </>
    );
  }

  if (view === 'cover') {
    return (
      <>
        <style>{CSS}</style>
        <Cover onStart={handleStart} existingUser={userName} />
        <div style={{textAlign:'center', padding:'16px 0 8px'}}>
          <button className="btn btn-secondary btn-sm"
            onClick={() => setView('coach')}
            style={{fontSize:'11px', color:'var(--taupe)'}}>
            Espace Coach
          </button>
        </div>
      </>
    );
  }


  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const navigateTo = (tabId) => {
    setActiveTab(tabId);
    scrollToTop();
  };

  // NavButtons supprimé — la navigation se fait via la bottom nav et les boutons contextuels dans chaque page

  const renderPage = () => {
    if (activeTab === 'profil') return (
      <ProfilPage data={journalData.profil || {}}
        onChange={v => updateSection('profil', v)} />
    );
    if (activeTab === 'engagements') return (
      <EngagementsPage data={journalData.engagements || {}}
        onChange={v => updateSection('engagements', v)} />
    );
    if (activeTab === 'serment') return (
      <SermentPage data={journalData.serment || {}}
        onChange={v => updateSection('serment', v)}
        userName={userName} />
    );
    if (activeTab === 'produits') return (
      <ProduitsPage data={journalData.produits_page || {}}
        onChange={v => updateSection('produits_page', v)} />
    );
    if (activeTab === 'sommaire') return (
      <SommairePage onNavigate={navigateTo} journalData={journalData} />
    );

    if (activeTab === 'mode-emploi') return (
      <ModeEmploiPage />
    );

    if (activeTab === 'faq') return (
      <FAQPage onContact={() => {}} />
    );

    if (activeTab === 'semaine-5') return (
      <WeekPage weekNum={5}
        data={journalData['semaine-5'] || {}}
        onChange={v => updateSection('semaine-5', v)}
        gestes={(journalData.engagements?.gestes) || [{},{},{}]}
        onNavigate={navigateTo} />
    );

    if (activeTab === 'semaine-6') return (
      <WeekPage weekNum={6}
        data={journalData['semaine-6'] || {}}
        onChange={v => updateSection('semaine-6', v)}
        gestes={(journalData.engagements?.gestes) || [{},{},{}]}
        onNavigate={navigateTo} />
    );

    if (activeTab === 'bilan') return (
      <BilanPage data={journalData.bilan || {}}
        onChange={v => updateSection('bilan', v)}
        userName={userName}
        journalData={journalData} />
    );
    if (activeTab.startsWith('semaine-')) {
      const weekNum = parseInt(activeTab.split('-')[1]);
      return (
        <WeekPage weekNum={weekNum}
          data={journalData[activeTab] || {}}
          onChange={v => updateSection(activeTab, v)}
          gestes={(journalData.engagements?.gestes) || [{},{},{}]}
          onNavigate={navigateTo} />
      );
    }
    if (activeTab.startsWith('day-')) {
      const dayNum = parseInt(activeTab.split('-')[1]);
      return (
        <DayPage dayNum={dayNum}
          data={journalData[activeTab] || {}}
          onChange={v => updateSection(activeTab, v)}
          gestes={(journalData.engagements?.gestes) || [{},{},{}]}
          onNavigate={navigateTo} />
      );
    }
    return null;
  };

  // Calcul progression

  if (view !== 'journal') return null;

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <div className="header">
          <div className="header-brand">
            <img
              src={process.env.PUBLIC_URL + '/logo_header.png'}
              alt="Missawoa"
              style={{ height: '40px', width: 'auto', borderRadius: '50%', objectFit: 'cover' }}
            />
            <div>
              <div className="header-title">Missawoa</div>
              <div className="header-sub">{userName}</div>
            </div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
            {saving && <span style={{fontSize:'10px', color:'var(--taupe)'}}>✦</span>}
            <div className="header-day">{daysCompleted}/42 jours</div>
          </div>
        </div>

        {/* Message coach */}
        <InstallBanner daysCompleted={daysCompleted} />
        {clientId && <CoachMessageBanner clientId={clientId} />}

        {/* Banniere offline */}
        {!isOnline && (
          <div style={{
            background:'#E65100', color:'white', padding:'8px 16px',
            fontSize:'12px', fontWeight:'700', textAlign:'center', letterSpacing:'0.5px'
          }}>
            Pas de connexion internet. Tes données sont sauvegardées localement et seront synchronisées dès que tu seras reconnectée.
          </div>
        )}

        {/* Barre de progression */}
        <div style={{padding:'8px 16px', background:'var(--creme-fonce)'}}>
          <div className="progress-bar">
            <div className="progress-fill" style={{width:`${(daysCompleted/42)*100}%`}} />
          </div>
        </div>

        {renderPage()}

        {/* ── Bottom Navigation ── */}
        <nav className="bottom-nav">
          {[
            { key: 'accueil', icon: '🏠', label: 'Accueil',
              action: () => setView('welcome') },
            { key: 'journal', icon: '📝', label: 'Journal',
              action: () => { navigateTo(`day-${prochainJourNav}`); } },
            { key: 'moi', icon: '👤', label: 'Moi',
              action: () => navigateTo('profil') },
            { key: 'plus', icon: '☰', label: 'Plus',
              action: () => navigateTo('sommaire') },
          ].map(item => (
            <button key={item.key}
              className={`bottom-nav-btn ${navSection === item.key ? 'active' : ''}`}
              onClick={item.action}>
              <span className="bottom-nav-icon">{item.icon}</span>
              <span className="bottom-nav-label">{item.label}</span>
              {navSection === item.key && <div className="bottom-nav-dot" />}
            </button>
          ))}
        </nav>

        <Toast msg={toast} />
      </div>
    </>
  );
}
